import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import socketService from '../../services/socket';
import { Chat, Group, User, Message } from '../../types';
import MessageContextMenu from './MessageContextMenu';
import './Dashboard.css';

const DASHBOARD_CURRENT_GROUP_KEY = 'dashboardCurrentGroup';
type LinkPreviewData = {
  url: string;
  final_url: string;
  hostname: string;
  site_name: string;
  title?: string | null;
  description?: string | null;
  image_url?: string | null;
};

const linkPreviewCache = new Map<string, LinkPreviewData | null>();

const getStoredDashboardGroup = (): number | null => {
  const rawValue = localStorage.getItem(DASHBOARD_CURRENT_GROUP_KEY);
  if (!rawValue) return null;
  const parsedValue = Number(rawValue);
  return Number.isInteger(parsedValue) ? parsedValue : null;
};

// Левое меню с профилем и группами
const Sidebar: React.FC<{
  groups: Group[];
  currentGroup: number | null;
  onGroupSelect: (groupId: number | null) => void;
  stats: { active: number; queue: number };
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}> = ({ groups, currentGroup, onGroupSelect, stats, mobileOpen, onMobileClose }) => {
  const { user, logout } = useAuth();
  const visibleGroups = groups.filter((group) => {
    const name = (group.group_name || '').toLowerCase();
    return !name.includes('без групп') && !name.includes('корзин');
  });

  // Сворачивающаяся секция «Управление» — состояние сохраняем в localStorage
  const [adminExpanded, setAdminExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sidebar_admin_expanded') === '1';
    } catch {
      return false;
    }
  });
  const toggleAdmin = () => {
    setAdminExpanded((prev) => {
      const next = !prev;
      try { localStorage.setItem('sidebar_admin_expanded', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  // Модалка «О программе»
  const [aboutOpen, setAboutOpen] = useState(false);
  const [versionInfo, setVersionInfo] = useState<any>(null);
  const bundleHash = (() => {
    try {
      const scripts = Array.from(document.querySelectorAll('script[src]'));
      for (const s of scripts) {
        const src = (s as HTMLScriptElement).src;
        const m = src.match(/\/static\/js\/main\.([a-f0-9]+)\.js/);
        if (m) return m[1];
      }
    } catch {}
    return '';
  })();
  useEffect(() => {
    if (aboutOpen && !versionInfo) {
      fetch('/api/health/version', { credentials: 'include' })
        .then((r) => r.json())
        .then(setVersionInfo)
        .catch(() => setVersionInfo({ error: 'нет данных' }));
    }
  }, [aboutOpen, versionInfo]);

  const handleNav = (path: string) => {
    if (onMobileClose) onMobileClose();
    window.location.href = path;
  };

  return (
    <>
      {mobileOpen && <div className="sidebar-backdrop" onClick={onMobileClose}></div>}
    <div className={`dashboard-sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
      {/* Профиль */}
      <div className="sidebar-profile">
        <div className="profile-container">
          <div className="profile-avatar">
            {user?.login?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="profile-info">
            <div className="profile-name">{user?.login}</div>
            <div className="profile-role">
              {user?.is_admin ? 'Администратор' : 'Оператор'}
            </div>
          </div>
          <div className="connection-status"></div>
          {onMobileClose && (
            <button className="sidebar-close-mobile" onClick={onMobileClose} aria-label="Закрыть">
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
      </div>

      {/* Статистика */}
      <div className="sidebar-stats">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Активные</div>
            <div className="stat-value">{stats.active}</div>
          </div>
          <div className="stat-card queue">
            <div className="stat-label">Очередь</div>
            <div className="stat-value">{stats.queue}</div>
          </div>
        </div>
      </div>

      {/* Группы */}
      <div className="sidebar-groups">
        <div className="groups-header">
          <div className="groups-title">ГРУППЫ</div>
        </div>
        <div className="groups-list">
          {visibleGroups.length === 0 ? (
            <div style={{padding: '1rem', color: '#9ca3af', fontSize: '0.875rem'}}>Нет групп</div>
          ) : (
            visibleGroups.map(group => {
              const unreadCount = group.unread_count || 0;
              return (
                <div
                  key={group.id}
                  className={`group-item ${currentGroup === group.id ? 'active' : ''}`}
                  onClick={() => { onGroupSelect(group.id); if (onMobileClose) onMobileClose(); }}
                >
                  <i className={`bi bi-${group.icon_code || 'diagram-3'}`}></i>
                  <span>{group.group_name}</span>
                  {unreadCount > 0 && (
                    <span className="group-unread">{unreadCount}</span>
                  )}
                </div>
              );
            })
          )}
        </div>
        {/* Ссылка на Почту в самом низу групп */}
        <div
          className="group-item"
          style={{ marginTop: '0.5rem', borderTop: '1px solid var(--color-section-borders)', paddingTop: '0.75rem' }}
          onClick={() => handleNav('/mail')}
        >
          <i className="bi bi-envelope-fill"></i>
          <span>Почта</span>
        </div>
      </div>

      {/* Администрирование — сворачивающееся */}
      {user?.is_admin && (
        <div className={`sidebar-admin ${adminExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="admin-header admin-toggle" onClick={toggleAdmin} style={{cursor: 'pointer'}}>
            <div className="admin-title">УПРАВЛЕНИЕ</div>
            <i className={`bi ${adminExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{marginLeft: 'auto', fontSize: '0.75rem', color: '#9ca3af'}}></i>
          </div>
          {adminExpanded && (
            <>
              <div className="admin-item" onClick={() => handleNav('/admin/users')} style={{cursor: 'pointer'}}>
                <i className="bi bi-people"></i>
                <span>Персонал</span>
              </div>
              <div className="admin-item" onClick={() => handleNav('/admin/groups')} style={{cursor: 'pointer'}}>
                <i className="bi bi-diagram-3"></i>
                <span>Группы</span>
              </div>
              <div className="admin-item" onClick={() => handleNav('/admin/tickets')} style={{cursor: 'pointer'}}>
                <i className="bi bi-ticket-perforated"></i>
                <span>Тикеты</span>
              </div>
              <div className="admin-item" onClick={() => handleNav('/admin/broadcast')} style={{cursor: 'pointer'}}>
                <i className="bi bi-megaphone"></i>
                <span>Рассылка</span>
              </div>
              <div className="admin-item" onClick={() => handleNav('/settings')} style={{cursor: 'pointer'}}>
                <i className="bi bi-gear"></i>
                <span>Настройки</span>
              </div>
              <div className="admin-item" onClick={() => handleNav('/admin/smtp')} style={{cursor: 'pointer'}}>
                <i className="bi bi-envelope-check"></i>
                <span>SMTP</span>
              </div>
              <div className="admin-item" onClick={() => handleNav('/admin/spell')} style={{cursor: 'pointer'}}>
                <i className="bi bi-spellcheck"></i>
                <span>Орфография</span>
              </div>
              <div className="admin-item" onClick={() => handleNav('/admin/trash')} style={{cursor: 'pointer'}}>
                <i className="bi bi-trash3"></i>
                <span>Корзина</span>
              </div>
              <div className="admin-item" onClick={() => setAboutOpen(true)} style={{cursor: 'pointer', borderTop: '1px solid var(--color-section-borders)', marginTop: '0.25rem', paddingTop: '0.5rem'}}>
                <i className="bi bi-info-circle"></i>
                <span>О программе</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Модалка «О программе» — стиль MikBill (описание + таблица параметров) */}
      {aboutOpen && (
        <div className="about-overlay" onClick={() => setAboutOpen(false)}>
          <div className="about-modal" onClick={(e) => e.stopPropagation()}>
            <div className="about-header">
              <h3><i className="bi bi-info-circle"></i> О программе</h3>
              <button className="about-close" onClick={() => setAboutOpen(false)}>✕</button>
            </div>
            <div className="about-body">
              <div className="about-intro">
                Программно-Аппаратный Комплекс<br/>
                Автоматизированная система поддержки клиентов<br/>
                <b>«itsupportbot»</b> (версия ПО v.{process.env.REACT_APP_VERSION || '0.1.0'})
              </div>

              <div className="about-table">
                <div className="about-row">
                  <span>название:</span>
                  <code>Интерфейс {user?.is_admin ? 'Администратора' : 'Оператора'}</code>
                </div>
                <div className="about-row">
                  <span>интерфейс:</span>
                  <code>
                    {process.env.REACT_APP_VERSION || '0.1.0'}
                    {bundleHash && <> · <b>{bundleHash}</b></>}
                    {process.env.REACT_APP_BUILD_TIME && (
                      <> ({process.env.REACT_APP_BUILD_TIME})</>
                    )}
                  </code>
                </div>
                <div className="about-row">
                  <span>backend:</span>
                  <code>
                    {versionInfo?.version || '—'}
                    {versionInfo?.build_time && (
                      <> ({versionInfo.build_time})</>
                    )}
                  </code>
                </div>
                <div className="about-row">
                  <span>ревизия БД:</span>
                  <code>{versionInfo?.db_revision || '—'}</code>
                </div>
                <div className="about-row">
                  <span>Python:</span>
                  <code>{versionInfo?.python || '—'} ({versionInfo?.platform || '—'})</code>
                </div>
                <div className="about-row">
                  <span>commit фронта:</span>
                  <code>{process.env.REACT_APP_GIT_COMMIT || '—'}</code>
                </div>
                <div className="about-row">
                  <span>лицензия:</span>
                  <code>Внутренняя</code>
                </div>
                <div className="about-row">
                  <span>чаты:</span>
                  <code>
                    {versionInfo?.stats
                      ? `${versionInfo.stats.active_chats} / ${versionInfo.stats.total_chats}`
                      : '—'}
                  </code>
                </div>
                <div className="about-row">
                  <span>операторы:</span>
                  <code>{versionInfo?.stats?.active_users ?? '—'}</code>
                </div>
                <div className="about-row">
                  <span>запущен:</span>
                  <code>{(() => {
                    const iso = versionInfo?.started_at_iso;
                    if (iso) {
                      const d = new Date(iso);
                      if (!isNaN(d.getTime())) return d.toLocaleString('ru-RU');
                    }
                    return versionInfo?.started_at || '—';
                  })()}</code>
                </div>
              </div>

              <div className="about-footer-info">
                © {new Date().getFullYear()} ituslugi.pro · FastAPI + React
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Выход */}
      <div className="sidebar-logout">
        <button onClick={logout} className="logout-btn">
          <i className="bi bi-box-arrow-right"></i>
          Выход
        </button>
      </div>
    </div>
    </>
  );
};

// Категории обращений (совпадают с базой Ticket.category из кнопок бота).
// `color` — насыщенный, для чипов фильтра (там цвет = выбранное действие).
// `soft`/`ink` — для бейджей в ленте: сплошная заливка #dc2626 у каждого бага
// делала строку красной наравне со счётчиком непрочитанных и просрочкой, и
// срочное переставало выделяться (замечание Николая 17.08 со скрином ленты).
const CATEGORY_META: Record<string, { label: string; emoji: string; color: string; soft: string; ink: string }> = {
  bug: { label: 'Баг', emoji: '🐛', color: '#dc2626', soft: '#fee2e2', ink: '#b91c1c' },
  feature: { label: 'Доработка', emoji: '🛠', color: '#7c3aed', soft: '#f3e8ff', ink: '#6d28d9' },
  install: { label: 'Установка', emoji: '📦', color: '#0891b2', soft: '#cffafe', ink: '#0e7490' },
  help: { label: 'Вопрос', emoji: '🆘', color: '#2563eb', soft: '#dbeafe', ink: '#1d4ed8' },
};

// Под-тег в ленте пишем по-русски: сырые слаги («payments», «other») не влезали
// в строку и обрезались до «aymen», «othe». «Прочее» не показываем вовсе — оно
// не сообщает ничего, а место занимает.
const SUB_WORDS: Record<string, string> = {
  payments: 'платежи', sorm: 'СОРМ', radius: 'Radius', ippool: 'IP-пулы',
  reports: 'отчёты', report: 'отчёт', lk: 'ЛК', ui: 'интерфейс',
  integration: 'интеграция', api: 'API', billing: 'биллинг',
  new: 'новая', migrate: 'миграция', update: 'обновление',
  other: '',
};
// База категории из "bug:payments" → "bug"
const baseCat = (c?: string | null): string | null => (c ? String(c).split(':')[0] : null);
// Под-тег категории из "bug:payments" → "payments" (null если нет)
const subCat = (c?: string | null): string | null => {
  if (!c) return null;
  const p = String(c).split(':');
  return p.length > 1 && p[1] ? p[1] : null;
};
// Phase B: список категорий обращения для чипов. Фолбэк на одиночную ticket_category.
// Возвращает только валидные (известные base) категории.
const ticketCats = (chat: { ticket_categories?: string[]; ticket_category?: string | null }): string[] => {
  const raw = (chat.ticket_categories && chat.ticket_categories.length > 0)
    ? chat.ticket_categories
    : (chat.ticket_category ? [chat.ticket_category] : []);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of raw) {
    const b = baseCat(c);
    if (b && CATEGORY_META[b] && !seen.has(c)) { seen.add(c); out.push(c); }
  }
  return out;
};

// Мост вставки текста в композер: MessagesArea (где живёт setMessageText) регистрирует
// сюда свою функцию, InfoPanel (рендерится в другом компоненте — DashboardNew) вызывает
// её для кнопки «Вставить в ответ» из RAG-подсказок. Иначе setMessageText вне области видимости.
const composerBridge: { insert: ((t: string) => void) | null } = { insert: null };

// Список чатов с табами и поиском
const ChatsList: React.FC<{
  chats: Chat[];
  groups: Group[];
  currentGroup: number | null;
  onGroupSelect: (groupId: number | null) => void;
  selectedChat: Chat | null;
  onChatSelect: (chat: Chat) => void;
  onToggleFavorite: (chat: Chat) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  currentGroupName: string;
  onMobileSidebarOpen?: () => void;
}> = ({ chats, groups, currentGroup, onGroupSelect, selectedChat, onChatSelect, onToggleFavorite, searchTerm, onSearchChange, currentGroupName, onMobileSidebarOpen }) => {
  // Чаты с ОТКРЫТЫМИ GitLab-ишью — поднимаем в списке наверх. Тянем set chat_id раз в минуту.
  const [openIssueChatIds, setOpenIssueChatIds] = useState<Set<number>>(new Set());
  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get('/api/gitlab-issues?state=opened')
        .then(r => {
          if (!alive) return;
          const ids = (r.data || [])
            .map((i: any) => i.chat_id)
            .filter((x: any) => x != null)
            .map((x: any) => Number(x));
          setOpenIssueChatIds(new Set(ids));
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const visibleGroups = groups.filter((group) => {
    const name = (group.group_name || '').toLowerCase();
    return !name.includes('без групп') && !name.includes('корзин');
  });
  
  // Helper function to get avatar URL
  const getAvatarUrl = (chat: Chat): string | null => {
    if (!chat.avatar_id) return null;
    
    const avatarId = String(chat.avatar_id);
    if (avatarId.startsWith('http://') || avatarId.startsWith('https://')) {
      return `/api/avatar?avatar_id=${encodeURIComponent(avatarId)}`;
    }
    return `/api/avatar/${avatarId}`;
  };

  const getChatChannel = (chat: Chat): 'MAX' | 'TG' | null => {
    if (!chat?.chat_id) return null;
    const chatId = String(chat.chat_id).toLowerCase();
    if (chatId.startsWith('max_')) return 'MAX';
    if (chatId.startsWith('telegram_')) return 'TG';
    if (/^\d+$/.test(chatId)) return 'TG';
    return null;
  };

  const selectedGroup = groups.find(g => g.id === currentGroup);
  const isStaffGroup = (selectedGroup?.group_name || '').toLowerCase().includes('персонал');

  // Фильтр по категории обращения (null = все) + просроченные по SLA + с открытыми issue.
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [slaBreachedOnly, setSlaBreachedOnly] = useState(false);
  const [issuesOnly, setIssuesOnly] = useState(false);

  // Счётчики по категориям + просрочкам + открытым issue (по чатам группы, до текст-поиска).
  const catCounts: Record<string, number> = { bug: 0, feature: 0, install: 0, help: 0 };
  let breachedCount = 0;
  let issuesCount = 0;
  chats.forEach(c => {
    const b = baseCat(c.ticket_category);
    if (b && catCounts[b] !== undefined) catCounts[b] += 1;
    if (c.sla_state === 'breached') breachedCount += 1;
    if (openIssueChatIds.has(Number(c.id))) issuesCount += 1;
  });

  const filteredChats = chats.filter(chat => {
    // Внутренние чаты показываем только в группе "Персонал"
    const chatId = String(chat.chat_id || '');
    if (!isStaffGroup && chatId.startsWith('internal_')) {
      return false;
    }

    if (categoryFilter && baseCat(chat.ticket_category) !== categoryFilter) return false;
    if (slaBreachedOnly && chat.sla_state !== 'breached') return false;
    if (issuesOnly && !openIssueChatIds.has(Number(chat.id))) return false;

    if (!searchTerm) return true;
    // Поиск по: имя/alias/ник/chat_id + компания/лицензия/ИНН/телефон. Ведущий «@» срезаем.
    const term = searchTerm.toLowerCase().replace(/^@+/, '').trim();
    if (!term) return true;
    const haystack = [
      chat.display_name, chat.full_name, chat.first_name, chat.last_name, chat.nickname,
      chat.alias, chat.company_name, chat.company_inn, chat.license_number, chat.phone,
      chat.chat_id,
    ].map(v => String(v || '').toLowerCase()).join(' ');
    return haystack.includes(term);
  });

  const sortedChats = [...filteredChats].sort((a, b) => {
    const pinDelta = Number(Boolean(b.is_pinned)) - Number(Boolean(a.is_pinned));
    if (pinDelta !== 0) return pinDelta;

    // Дальше — строго по времени последнего сообщения: лента должна начинаться
    // самыми свежими перепиской (замечание Николая 17.08). Раньше над временем
    // стояли два яруса — «есть непрочитанные» и «есть открытый GitLab-issue», и
    // второй выносил наверх чаты месячной давности (Пупкин 04.08, Ivan 29.07)
    // просто потому, что задача в трекере ещё не закрыта. Непрочитанные и так
    // свежие по времени, а «где есть issue» отбирается кнопкой «🐞 Issue».
    const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
    const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
    return bTime - aTime;
  });

  const titleCount = searchTerm && filteredChats.length !== chats.length
    ? `${filteredChats.length}/${chats.length}`
    : String(chats.length);

  return (
    <div className="chats-container">
      <div className="chats-header">
        <div className="chats-title-row">
          {onMobileSidebarOpen && (
            <button
              type="button"
              className="mobile-menu-btn"
              onClick={onMobileSidebarOpen}
              aria-label="Меню"
            >
              <i className="bi bi-list"></i>
            </button>
          )}
          <h5 className="chats-title">{currentGroupName}</h5>
          <span className="chats-count-badge">{titleCount}</span>
        </div>
        <div className="mobile-group-selector">
          <select
            className="mobile-group-select"
            value={currentGroup ?? ''}
            onChange={(e) => onGroupSelect(e.target.value ? Number(e.target.value) : null)}
          >
            {visibleGroups.map((group) => (
              <option key={group.id} value={group.id}>{group.group_name}</option>
            ))}
          </select>
        </div>
        
        {/* Поиск */}
        <div className="chat-search-container">
          <i className="bi bi-search chat-search-icon"></i>
          <input
            type="text"
            className="chat-search"
            placeholder="Поиск..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* Фильтр по категории обращения + SLA-просрочка */}
        <div className="cat-filter-chips">
          <button
            type="button"
            className={`cat-chip ${categoryFilter === null ? 'active' : ''}`}
            onClick={() => setCategoryFilter(null)}
          >
            Все
          </button>
          {Object.entries(CATEGORY_META).map(([key, meta]) => (
            <button
              key={key}
              type="button"
              className={`cat-chip ${categoryFilter === key ? 'active' : ''}`}
              style={categoryFilter === key ? { background: meta.color, borderColor: meta.color, color: '#fff' } : { borderColor: meta.color, color: meta.color }}
              onClick={() => setCategoryFilter(categoryFilter === key ? null : key)}
              title={meta.label}
            >
              {meta.emoji} {meta.label}{catCounts[key] ? ` ${catCounts[key]}` : ''}
            </button>
          ))}
          <button
            type="button"
            className={`cat-chip cat-chip-sla ${slaBreachedOnly ? 'active' : ''}`}
            onClick={() => setSlaBreachedOnly(v => !v)}
            title="Только просроченные по SLA"
          >
            ⏰ Просрочен{breachedCount ? ` ${breachedCount}` : ''}
          </button>
          <button
            type="button"
            className={`cat-chip cat-chip-issue ${issuesOnly ? 'active' : ''}`}
            onClick={() => setIssuesOnly(v => !v)}
            title="С открытыми issue в GitLab"
          >
            🐞 Issue{issuesCount ? ` ${issuesCount}` : ''}
          </button>
        </div>
      </div>

      <div className="chats-list">
        {filteredChats.length === 0 ? (
          <div style={{padding: '2rem', textAlign: 'center', color: '#9ca3af'}}>
            Нет чатов в этой группе
          </div>
        ) : (
            sortedChats.map(chat => {
              const status = chat.status || (chat.is_active === false ? 'closed' : 'open');
              const hasNewAfterResolve = status === 'closed' && Boolean(chat.chat_metadata?.has_new_message_after_resolve);
              const displayName = chat.display_name || chat.full_name || chat.first_name || chat.nickname || `User ${chat.chat_id}`;
              const firstLetter = displayName[0]?.toUpperCase() || 'U';
              const avatarUrl = getAvatarUrl(chat);
              const channel = getChatChannel(chat);
              
              return (
              <div
                key={chat.id}
                className={`chat-item ${selectedChat?.id === chat.id ? 'selected' : ''}`}
                onClick={() => onChatSelect(chat)}
              >
                <div className="chat-item-avatar">
                  <div className="info-avatar-fallback" style={{ fontSize: '0.875rem' }}>{firstLetter}</div>
                  {avatarUrl ? (
                    <img 
                      src={avatarUrl} 
                      alt={displayName}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                      style={{width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%'}}
                    />
                  ) : null}
                </div>
                <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem', width: 0}}>
                  <div className="chat-item-header">
                    <div className="chat-user-name-wrap">
                      <span className="chat-user-name">
                        {displayName}
                      </span>
                      {channel && (
                        <span className={`channel-badge ${channel === 'MAX' ? 'channel-badge-max' : 'channel-badge-tg'}`}>
                          {channel}
                        </span>
                      )}
                      {/* 🐞 «есть открытый issue» стоял вплотную к 🐛 «баг» — два жучка
                          подряд читались как один шум. Показываем его только там, где
                          бейджа с багом нет. */}
                      {openIssueChatIds.has(Number(chat.id))
                        && !ticketCats(chat).some(c => baseCat(c) === 'bug') && (
                        <span title="Есть открытые GitLab-issue" style={{ marginLeft: 4 }}>🐞</span>
                      )}
                      {ticketCats(chat).map((cat, i) => {
                        const b = baseCat(cat)!;
                        const meta = CATEGORY_META[b];
                        const sub = subCat(cat);
                        const subWord = sub ? (SUB_WORDS[sub] ?? sub) : '';
                        const hasIssue = openIssueChatIds.has(Number(chat.id));
                        return (
                          <span
                            key={cat}
                            className="ticket-cat-badge"
                            style={{ background: meta.soft, color: meta.ink }}
                            title={`${meta.label}${sub ? ' · ' + (SUB_WORDS[sub] || sub) : ''}${hasIssue ? ' · есть открытый issue в GitLab' : ''}${chat.ticket_priority ? ` · приоритет ${chat.ticket_priority}` : ''}${chat.ticket_category_source === 'ai' ? ' · определено AI' : ''}`}
                          >
                            {meta.emoji}
                            {subWord && <span className="cat-sub-mark">{subWord}</span>}
                            {i === 0 && chat.ticket_category_source === 'ai' && <span className="cat-ai-mark">AI</span>}
                          </span>
                        );
                      })}
                      {chat.sla_state === 'breached' && (
                        <span className="sla-badge sla-breached" title={`SLA просрочен${chat.sla_due_at ? ' (до ' + new Date(chat.sla_due_at).toLocaleString('ru-RU') + ')' : ''}`}>⏰</span>
                      )}
                      {chat.sla_state === 'warning' && (
                        <span className="sla-badge sla-warning" title="SLA скоро истечёт">⏳</span>
                      )}
                    </div>
                      <div className="chat-item-actions">
                        <button
                          type="button"
                          className={`chat-favorite-btn ${chat.is_pinned ? 'active' : ''}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onToggleFavorite(chat);
                          }}
                          title={chat.is_pinned ? 'Убрать из избранного' : 'Добавить в избранное'}
                          aria-label={chat.is_pinned ? 'Убрать из избранного' : 'Добавить в избранное'}
                        >
                          <i className={`bi ${chat.is_pinned ? 'bi-star-fill' : 'bi-star'}`}></i>
                        </button>
                      </div>
                  </div>
                  {chat.alias && (
                    <div className="chat-item-info">
                      <span className="chat-alias">{chat.alias}</span>
                    </div>
                  )}
                  {chat.assigned_to_name && (
                    <div className="chat-assigned">
                      👤 {chat.assigned_to_name}
                    </div>
                  )}
                  {chat.last_message && (
                    <div className="chat-last-message" title={chat.last_message}>
                      {chat.last_message}
                    </div>
                  )}
                  <div className="chat-item-footer">
                    <span className={`chat-status status-${status} ${hasNewAfterResolve ? 'status-needs-resume' : ''}`}>
                      {status === 'open'
                        ? (chat.chat_metadata?.in_work ? '🟢 В работе' : '🟢 Открыт')
                        : '🔴 Закрыт'}
                    </span>
                    {hasNewAfterResolve && (
                      <span className="chat-reopen-badge">Новое после закрытия</span>
                    )}
                    {(chat.last_message_at || chat.unread_count > 0) && (
                      <span className="chat-time-col">
                        {chat.last_message_at && (
                          <span className="chat-time">
                            {new Date(chat.last_message_at).toLocaleString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        )}
                        {chat.unread_count > 0 && (
                          <span className="unread-badge unread-under-time" title="Новые сообщения">
                            {chat.unread_count}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const LinkPreviewCard: React.FC<{ url: string }> = ({ url }) => {
  const [preview, setPreview] = useState<LinkPreviewData | null | undefined>(() => {
    if (linkPreviewCache.has(url)) {
      return linkPreviewCache.get(url) ?? null;
    }
    return undefined;
  });
  const [imageVisible, setImageVisible] = useState(false);

  useEffect(() => {
    if (linkPreviewCache.has(url)) {
      setPreview(linkPreviewCache.get(url) ?? null);
      return;
    }

    let cancelled = false;
    api.get('/api/chats/preview/link', { params: { url } })
      .then((response) => {
        if (cancelled) return;
        const payload = response.data && typeof response.data === 'object' && 'url' in response.data
          ? response.data as LinkPreviewData
          : null;
        linkPreviewCache.set(url, payload);
        setPreview(payload);
      })
      .catch(() => {
        if (cancelled) return;
        linkPreviewCache.set(url, null);
        setPreview(null);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    setImageVisible(Boolean(preview?.image_url));
  }, [preview?.image_url]);

  if (preview === null) return null;

  const resolvedUrl = preview?.final_url || url;
  const fallbackHost = (() => {
    try {
      return new URL(resolvedUrl).hostname.replace(/^www\./i, '');
    } catch {
      return resolvedUrl;
    }
  })();
  const siteName = preview?.site_name || fallbackHost;
  const humanizeFallbackTitle = (value: string) => {
    const compact = (value || '').trim();
    if (!compact) return fallbackHost;
    const words = compact
      .replace(/[|+]+/g, ' ')
      .replace(/\s+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .slice(0, 12)
      .map((w) => (w.length > 2 ? w[0].toUpperCase() + w.slice(1) : w.toUpperCase()));
    return words.join(' ');
  };

  const title = humanizeFallbackTitle(preview?.title || fallbackHost);
  const description = preview?.description || '';
  const hasImage = imageVisible && Boolean(preview?.image_url);
  const fallbackLetter = ((siteName || fallbackHost || 'L')[0] || 'L').toUpperCase();

  return (
    <div className="link-preview-box mt-2">
      <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="link-preview-card tg-link-preview-card">
        <div className="link-preview-meta">
          <div className="link-preview-site">{siteName}</div>
          <div className="link-preview-title">{title}</div>
          {description && <div className="link-preview-description">{description}</div>}
          <div className="link-preview-domain">{fallbackHost}</div>
        </div>
        {hasImage && preview?.image_url && (
          <div className="link-preview-image-wrap">
            <img
              src={preview.image_url}
              alt={title}
              className="link-preview-image"
              onError={() => setImageVisible(false)}
            />
          </div>
        )}
        {!hasImage && (
          <div className="link-preview-fallback-image" aria-hidden="true">
            <div className="link-preview-fallback-letter">{fallbackLetter}</div>
          </div>
        )}
      </a>
    </div>
  );
};

// Область сообщений
const MessagesArea: React.FC<{
  chat: Chat | null;
  onMessageSent: () => void;
  showInfo: boolean;
  onToggleInfo: () => void;
  messagesReloadTrigger: number;
  onBackToList?: () => void;
  jumpToMessageId?: number | null;
  onJumpHandled?: () => void;
}> = ({ chat, onMessageSent, showInfo, onToggleInfo, messagesReloadTrigger, onBackToList, jumpToMessageId, onJumpHandled }) => {
  const quickEmojis = [
    '😀','😁','😂','🤣','😊','😍','😘','😎','🤔','😭','😡','👍','👎','👏','🙏','💪',
    '🔥','💯','✅','❌','⭐','🎉','🚀','💬','📌','❤️','💙','💚','💛','🧡','💜','🤍',
    '🤝','👌','🤌','👀','🙌','🎯','📎','📷','📄','☕','🍪','🛠️','⚙️','⏰','📞','📍'
  ];

  const [messages, setMessages] = useState<Message[]>([]);
  const [pinnedMessages, setPinnedMessages] = useState<Message[]>([]);
  const pinnedMessagesRef = useRef<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [translations, setTranslations] = useState<Record<number, string>>({});
  const [translating, setTranslating] = useState<Record<number, boolean>>({});
  // Видео #28: для переведённых входящих показываем RU основным, оригинал свёрнут.
  const [showOriginal, setShowOriginal] = useState<Record<number, boolean>>({});
  const [transcriptions, setTranscriptions] = useState<Record<string, string>>({});
  const [transcribing, setTranscribing] = useState<Record<string, boolean>>({});
  const [chatLang, setChatLang] = useState<string>('auto');
  const [messageText, setMessageText] = useState('');
  // Регистрируем вставку в композер для RAG-кнопки «Вставить в ответ» (см. composerBridge)
  useEffect(() => {
    composerBridge.insert = (t: string) => setMessageText(prev => (prev && prev.trim() ? prev + '\n' + t : t));
    return () => { composerBridge.insert = null; };
  }, []);
  const [messageSearch, setMessageSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<{message: Message; x: number; y: number} | null>(null);
  const [filePreview, setFilePreview] = useState<{url: string; fileName: string} | null>(null);
  const [spellNotice, setSpellNotice] = useState<string | null>(null);
  const [autoSpellOn, setAutoSpellOn] = useState<boolean>(localStorage.getItem('autoSpell') !== 'off');
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const initialScrollRef = useRef<boolean>(true);
  // Чат, для которого лента уже открыта. Нужен, чтобы отличить СМЕНУ чата (тогда
  // прыгаем вниз, к свежим сообщениям) от простой перезагрузки ленты того же чата
  // (тогда позицию скролла сохраняем).
  const loadedChatIdRef = useRef<number | null>(null);
  const preserveScrollRef = useRef<boolean>(false);
  const shouldStickToBottomRef = useRef<boolean>(true);
  const previousDistanceFromBottomRef = useRef<number>(0);
  // Позиция чтения (отступ сверху) на момент перезагрузки ленты.
  const previousScrollTopRef = useRef<number>(0);

  // Авто-высота поля ввода чата: растёт под объём текста (до 220px, дальше скролл).
  useEffect(() => {
    const el = messageInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 220) + 'px';
  }, [messageText]);

  useEffect(() => {
    setReplyToMessage(null);
  }, [chat?.id]);

  useEffect(() => {
    if (chat) {
      // 🔴 Только при смене чата. Раньше здесь стояло безусловное true, и любая
      // перезагрузка ленты считалась «чат только что открыли» → безусловный скролл вниз.
      // Лента перезагружается страховочным опросом РАЗ В 25 СЕКУНД, поэтому оператор,
      // читающий историю, каждые полминуты улетал в самый низ (жалоба владельца 29.08.2026).
      const chatChanged = loadedChatIdRef.current !== chat.id;
      loadedChatIdRef.current = chat.id;
      initialScrollRef.current = chatChanged;
      // Восстанавливаем из кэша сначала, если есть
      const cached = localStorage.getItem(`pinnedMessages_${chat.id}`);
      if (cached) {
        try {
          const pinned = JSON.parse(cached);
          setPinnedMessages(pinned);
          pinnedMessagesRef.current = pinned;
        } catch (e) {
          console.error('Ошибка восстановления из кэша:', e);
        }
      }
      loadMessages();
    } else {
      loadedChatIdRef.current = null;
      setMessages([]);
      setPinnedMessages([]);
    }
    // Инициализируем язык из метаданных чата
    const meta = (chat as any)?.chat_metadata || {};
    setChatLang(meta.reply_language || 'auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.id, messagesReloadTrigger]);

  useEffect(() => {
    if (initialScrollRef.current) {
      scrollToBottom('auto');
      initialScrollRef.current = false;
      shouldStickToBottomRef.current = true;
      preserveScrollRef.current = false;
    } else {
      const container = messagesContainerRef.current;
      if (!container) {
        return;
      }

      if (preserveScrollRef.current) {
        // Возвращаем ровно ту же позицию сверху. Раньше считали от НИЗА
        // (scrollHeight - clientHeight - расстояние_до_низа) — но лента отдаётся целиком и
        // новые сообщения дописываются снизу, поэтому при каждом новом сообщении текст
        // уезжал вверх под читателем, хотя формально «расстояние до низа» сохранялось.
        container.scrollTop = Math.max(0, previousScrollTopRef.current);
        preserveScrollRef.current = false;
        shouldStickToBottomRef.current = false;
        return;
      }

      if (shouldStickToBottomRef.current) {
        scrollToBottom('smooth');
        shouldStickToBottomRef.current = false;
        return;
      }

      // Автоскролл только если пользователь уже у нижней границы.
      // Это предотвращает "прыжок" после редактирования/обновления.
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      const isNearBottom = distanceFromBottom <= 80;
      if (isNearBottom) {
        scrollToBottom('smooth');
      }
    }
  }, [messages]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!showEmojiPicker) return;
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showEmojiPicker]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setFilePreview(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadMessages = async () => {
    if (!chat) return;

    const container = messagesContainerRef.current;
    if (container) {
      const distanceFromBottom = Math.max(
        0,
        container.scrollHeight - container.scrollTop - container.clientHeight
      );
      previousDistanceFromBottomRef.current = distanceFromBottom;
      previousScrollTopRef.current = container.scrollTop;
      shouldStickToBottomRef.current = initialScrollRef.current || distanceFromBottom <= 80;
      preserveScrollRef.current = !shouldStickToBottomRef.current;
    } else {
      previousDistanceFromBottomRef.current = 0;
      previousScrollTopRef.current = 0;
      shouldStickToBottomRef.current = true;
      preserveScrollRef.current = false;
    }

    const showLoader = messages.length === 0;
    if (showLoader) {
      setLoading(true);
    }

    try {
      const response = await api.get(`/api/chats/${chat.id}/messages`);
      const msgs = response.data;
      setMessages(msgs);
      // Обновляем закрепленные сообщения
      const pinned = msgs.filter((m: Message) => Boolean(m.flags?.pin));
      setPinnedMessages(pinned);
      pinnedMessagesRef.current = pinned; // Сохраняем в ref для быстрого доступа
      // Сохраняем в localStorage для восстановления при разрыве соединения
      if (pinned.length > 0) {
        localStorage.setItem(`pinnedMessages_${chat.id}`, JSON.stringify(pinned));
      }
    } catch (error) {
      console.error('Ошибка загрузки сообщений:', error);
      // Восстанавливаем из кэша при ошибке
      if (chat) {
        const cached = localStorage.getItem(`pinnedMessages_${chat.id}`);
        if (cached) {
          try {
            const pinned = JSON.parse(cached);
            setPinnedMessages(pinned);
            pinnedMessagesRef.current = pinned;
          } catch (e) {
            console.error('Ошибка восстановления из кэша:', e);
          }
        }
      }
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  // Добавляем новое сообщение локально без полной перезагрузки
  const addMessageLocally = (message: Message) => {
    setMessages(prev => [...prev, message]);
  };

  // Прокрутить к сообщению по ID
  const scrollToMessage = (messageId: number) => {
    const element = document.getElementById(`message-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMessageId(messageId);
      // Убираем подсветку через 2 секунды
      setTimeout(() => setHighlightedMessageId(null), 2000);
    }
  };

  useEffect(() => {
    if (jumpToMessageId) {
      setTimeout(() => {
        scrollToMessage(jumpToMessageId);
        onJumpHandled?.();
      }, 150);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpToMessageId]);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Функция для получения иконки файла по типу
  const getFileIcon = (fileName: string, mimeType: string, fileType: string) => {
    const name = fileName.toLowerCase();
    const mime = mimeType.toLowerCase();
    const type = fileType.toLowerCase();
    
    // Документы
    if (name.endsWith('.pdf') || mime.includes('pdf')) return 'bi-file-earmark-pdf';
    if (name.endsWith('.doc') || name.endsWith('.docx') || mime.includes('word') || mime.includes('document')) return 'bi-file-earmark-word';
    if (name.endsWith('.xls') || name.endsWith('.xlsx') || mime.includes('excel') || mime.includes('spreadsheet')) return 'bi-file-earmark-excel';
    if (name.endsWith('.ppt') || name.endsWith('.pptx') || mime.includes('powerpoint') || mime.includes('presentation')) return 'bi-file-earmark-ppt';
    
    // Текстовые файлы
    if (name.endsWith('.txt') || mime.includes('text/plain')) return 'bi-file-earmark-text';
    if (name.endsWith('.csv') || mime.includes('csv')) return 'bi-file-earmark-spreadsheet';
    
    // Архивы
    if (name.endsWith('.zip') || name.endsWith('.rar') || name.endsWith('.7z') || name.endsWith('.tar') || name.endsWith('.gz') || 
        mime.includes('zip') || mime.includes('archive') || mime.includes('compressed')) return 'bi-file-earmark-zip';
    
    // Аудио
    if (name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.ogg') || name.endsWith('.flac') || 
        mime.includes('audio') || type === 'voice') return 'bi-file-earmark-music';
    
    // Видео
    if (name.endsWith('.mp4') || name.endsWith('.avi') || name.endsWith('.mov') || name.endsWith('.mkv') || 
        mime.includes('video') || type === 'video') return 'bi-file-earmark-play';
    
    // Изображения
    if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.gif') || 
        name.endsWith('.webp') || name.endsWith('.svg') || mime.includes('image') || type === 'photo') return 'bi-file-earmark-image';
    
    // По умолчанию
    return 'bi-file-earmark';
  };

  // Функция для получения цвета иконки по типу файла
  const getFileIconColor = (fileName: string, mimeType: string, fileType: string) => {
    const name = fileName.toLowerCase();
    const mime = mimeType.toLowerCase();
    const type = fileType.toLowerCase();
    
    // PDF - красный
    if (name.endsWith('.pdf') || mime.includes('pdf')) return '#f44336';
    // Документы Word - синий
    if (name.endsWith('.doc') || name.endsWith('.docx') || mime.includes('word') || mime.includes('document')) return '#2196f3';
    // Excel - зеленый
    if (name.endsWith('.xls') || name.endsWith('.xlsx') || mime.includes('excel') || mime.includes('spreadsheet')) return '#4caf50';
    // PowerPoint - оранжевый
    if (name.endsWith('.ppt') || name.endsWith('.pptx') || mime.includes('powerpoint') || mime.includes('presentation')) return '#ff9800';
    // Архивы - фиолетовый
    if (name.endsWith('.zip') || name.endsWith('.rar') || name.endsWith('.7z') || name.endsWith('.tar') || name.endsWith('.gz') || 
        mime.includes('zip') || mime.includes('archive') || mime.includes('compressed')) return '#9c27b0';
    // Аудио - розовый
    if (name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.ogg') || name.endsWith('.flac') || 
        mime.includes('audio') || type === 'voice') return '#e91e63';
    // Видео - индиго
    if (name.endsWith('.mp4') || name.endsWith('.avi') || name.endsWith('.mov') || name.endsWith('.mkv') || 
        mime.includes('video') || type === 'video') return '#3f51b5';
    // Изображения - голубой
    if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.gif') || 
        name.endsWith('.webp') || name.endsWith('.svg') || mime.includes('image') || type === 'photo') return '#00bcd4';
    
    // По умолчанию - серый
    return '#757575';
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!messageText.trim() || !chat || sending) return;

    setSending(true);
    try {
      // Пайплайн перед отправкой:
      //   1. Spell-check (Gemini) — если autoSpellOn=true
      //   2. Translate (Gemini) — если chatLang ≠ 'auto'
      // Любые ошибки сети — тихий фолбэк к предыдущему тексту.
      let textToSend = messageText.trim();
      const originalText = textToSend;
      let afterSpell = textToSend;
      let didCorrect = false;
      let didTranslate = false;

      if (autoSpellOn && textToSend.length >= 3) {
        try {
          const sp = await api.post('/api/spell/check', { text: textToSend, chat_id: chat.id });
          if (sp.data?.success && sp.data?.changed && sp.data?.corrected) {
            textToSend = sp.data.corrected;
            afterSpell = sp.data.corrected;
            didCorrect = true;
          }
        } catch { /* отправим оригинал */ }
      }

      if (chatLang && chatLang !== 'auto' && textToSend.length >= 1) {
        try {
          const tr = await api.post('/api/translate', {
            text: textToSend,
            target_lang: chatLang,
          });
          const translated = tr.data?.translated || tr.data?.text;
          if (translated && translated.trim() && translated.trim() !== textToSend.trim()) {
            textToSend = translated.trim();
            didTranslate = true;
          }
        } catch { /* отправим без перевода */ }
      }

      // Сводный notice оператору (3 сек)
      let correctedNotice: string | null = null;
      if (didCorrect && didTranslate) {
        correctedNotice = `Орфография + Перевод:\n${originalText}\n↓\n${afterSpell}\n↓ ${chatLang.toUpperCase()}\n${textToSend}`;
      } else if (didCorrect) {
        correctedNotice = `Орфография:\n${originalText}\n↓\n${textToSend}`;
      } else if (didTranslate) {
        correctedNotice = `Перевод → ${chatLang.toUpperCase()}:\n${originalText}\n↓\n${textToSend}`;
      }

      if (editingMessageId) {
        await api.put(`/api/messages/${editingMessageId}`, { text: textToSend });
        setMessages(prev => prev.map(m => (
          m.id === editingMessageId ? { ...m, text: textToSend, is_edited: true } : m
        )));
        setEditingMessageId(null);
        setMessageText('');
      } else {
        await api.post('/api/messages', {
          chat_id: chat.chat_id,
          text: textToSend,
          // Видео #28: при переводе сохраняем исходный RU оператора для дашборда
          original_text: didTranslate ? afterSpell : undefined,
          reply_to: replyToMessage?.id ?? null,
          sender: 'operator'
        });

        setMessageText('');
        setReplyToMessage(null);
        await loadMessages();
        onMessageSent();
      }

      // Лёгкое уведомление о коррекции/переводе (5 сек), не блокирует поток.
      if (correctedNotice) {
        setSpellNotice(correctedNotice);
        setTimeout(() => setSpellNotice(null), 5000);
      }
    } catch (error) {
      console.error(editingMessageId ? 'Ошибка редактирования:' : 'Ошибка отправки сообщения:', error);
      alert(editingMessageId ? 'Ошибка редактирования сообщения' : 'Ошибка отправки сообщения');
    } finally {
      setSending(false);
    }
  };

  const handleFilesUpload = async (files: FileList | File[]) => {
    if (!files || files.length === 0 || !chat) return;
    const maxSize = 20 * 1024 * 1024;
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > maxSize) {
        alert(`Файл ${files[i].name} превышает 20MB`);
        return;
      }
    }
    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('file', files[i]);
      }
      await api.post(`/api/chats/${chat.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await loadMessages();
      onMessageSent();
    } catch (error) {
      console.error('Ошибка загрузки файлов:', error);
      alert('Ошибка загрузки файлов');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFilesUpload(e.target.files);
  };

  // Drag-over overlay: счётчик нужен потому что dragenter/dragleave стреляют
  // при переходе между дочерними элементами
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  const handleTranscribe = async (attKey: string, att: any) => {
    setTranscribing(prev => ({ ...prev, [attKey]: true }));
    try {
      const body = att.file_id
        ? { file_id: att.file_id }
        : { file_hash: att.hash };
      const resp = await api.post('/api/translate/transcribe', body);
      setTranscriptions(prev => ({ ...prev, [attKey]: resp.data.text || '(тишина)' }));
    } catch {
      setTranscriptions(prev => ({ ...prev, [attKey]: '⚠️ Ошибка расшифровки' }));
    } finally {
      setTranscribing(prev => ({ ...prev, [attKey]: false }));
    }
  };

  const handleSetChatLang = async (lang: string) => {
    if (!chat) return;
    setChatLang(lang);
    try {
      await api.post(`/api/chats/${chat.id}/language`, { lang: lang === 'auto' ? null : lang });
    } catch (e) {
      console.error('Failed to set chat language', e);
    }
  };

  const handleTranslate = async (messageId: number, text: string) => {
    setTranslating(prev => ({ ...prev, [messageId]: true }));
    try {
      const resp = await api.post('/api/translate', { text, target_lang: 'ru' });
      const translated = (resp.data?.translated ?? resp.data?.text ?? '').trim();
      // ВАЖНО: пишем всегда ОПРЕДЕЛЁННОЕ значение. Если перевод пустой/совпал с
      // оригиналом (сообщение уже на целевом языке) — кладём '' как «переведено,
      // показывать нечего». Иначе translations[id] остаётся undefined, гард
      // автоперевода снова его берёт на каждом поллинге messages → бесконечный
      // флуд /api/translate.
      setTranslations(prev => ({
        ...prev,
        [messageId]: (translated && translated !== text.trim()) ? translated : '',
      }));
    } catch {
      setTranslations(prev => ({ ...prev, [messageId]: '⚠️ Ошибка перевода' }));
    } finally {
      setTranslating(prev => ({ ...prev, [messageId]: false }));
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) {
      dragCounter.current++;
      setIsDragOver(true);
    }
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragOver(false);
    }
  };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const handleOverlayDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragOver(false);
    if (chatStatus !== 'open') return;
    const files = e.dataTransfer.files;
    if (files && files.length > 0) handleFilesUpload(files);
  };

  const handleEditMessage = async (message: Message) => {
    setEditingMessageId(message.id);
    setReplyToMessage(null);
    setMessageText(message.text || '');
    requestAnimationFrame(() => {
      messageInputRef.current?.focus();
    });
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setMessageText('');
  };;

  const handleReplyToMessage = (message: Message) => {
    setEditingMessageId(null);
    setReplyToMessage(message);
    requestAnimationFrame(() => {
      messageInputRef.current?.focus();
    });
  };

  const cancelReply = () => {
    setReplyToMessage(null);
  };

  const isOperatorMessage = (message: Message) => {
    const fromUser = String(message.from_user || '');
    return message.from_user === 'staff' || Boolean(message.local_user_id) || fromUser.startsWith('freescout_operator:');
  };

  // Авто-перевод входящих (БЕЗОПАСНО, без burst): если у чата задан язык
  // (chatLang ≠ 'auto'), переводим на ru только ПОСЛЕДНИЕ ~20 непереведённых
  // сообщений клиента, ПОСЛЕДОВАТЕЛЬНО (по одному), новые — по мере поступления.
  // Гард autoTrRunning не даёт запускать параллельно / зацикливаться.
  const autoTrRunning = useRef(false);
  useEffect(() => {
    if (!chatLang || chatLang === 'auto') return;
    // Дебаунс: поллинг messages часто отдаёт новый массив — не дёргаем перевод на
    // каждый апдейт, ждём 500 мс тишины и переводим пачкой (совместно с кэш-гардом
    // в handleTranslate это убирает флуд /api/translate).
    const handle = setTimeout(() => {
      if (autoTrRunning.current) return;
      const pending = messages
        .filter(m => !isOperatorMessage(m) && m.text && m.text.trim()
          && !(m.original_text && m.original_text !== m.text)
          && translations[m.id] === undefined && !translating[m.id])
        .slice(-20);
      if (pending.length === 0) return;
      autoTrRunning.current = true;
      (async () => {
        try {
          for (const m of pending) {
            await handleTranslate(m.id, m.text as string);
          }
        } finally {
          autoTrRunning.current = false;
        }
      })();
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, chatLang]);

  const getMessageSenderName = (message: Message) => {
    const isOperator = isOperatorMessage(message);
    if (isOperator) {
      return message.operator_pseudonym || message.sender_name || 'Оператор';
    }
    return message.sender_name || 'Пользователь';
  };

  const getDeliveryMarker = (message: Message, isOperator: boolean) => {
    const chatKey = String(chat?.chat_id || '');
    if (!isOperator || chatKey.startsWith('internal_')) {
      return null;
    }

    if (message.read_at) {
      return {
        icon: 'bi-check2-all',
        className: 'message-delivery-read',
        title: 'Прочитано',
      };
    }

    if (message.message_id || message.external_message_id) {
      return {
        icon: 'bi-check2-all',
        className: 'message-delivery-delivered',
        title: 'Доставлено',
      };
    }

    return {
      icon: 'bi-check2',
      className: 'message-delivery-sent',
      title: 'Отправлено',
    };
  };

  const getEditingPreviewText = () => {
    if (!editingMessageId) return 'Редактирование сообщения';
    const editingMessage = messages.find((m) => m.id === editingMessageId);
    const sourceText = (editingMessage?.text || '').trim();
    if (!sourceText) return 'Редактирование сообщения';
    return sourceText.length > 120 ? `${sourceText.slice(0, 120)}…` : sourceText;
  };

  const getReplyPreview = (replyToId?: number) => {
    if (!replyToId) {
      return { sender: 'Сообщение', text: 'Ответ на сообщение' };
    }

    const replyMessage = messages.find((m) => m.id === replyToId);
    if (!replyMessage) {
      return { sender: 'Сообщение', text: 'Ответ на сообщение' };
    }

    let previewText = (replyMessage.text || '').trim();

    if (!previewText && replyMessage.attachments && replyMessage.attachments.length > 0) {
      const firstAtt = replyMessage.attachments[0];
      previewText = firstAtt.file_name || (firstAtt.type ? `[${firstAtt.type}]` : 'Вложение');
    }

    if (!previewText) {
      previewText = 'Ответ на сообщение';
    }

    return {
      sender: getMessageSenderName(replyMessage),
      text: previewText.length > 120 ? `${previewText.slice(0, 120)}…` : previewText
    };
  };

  const insertEmoji = (emoji: string) => {
    const input = messageInputRef.current;

    if (!input) {
      setMessageText(prev => prev + emoji);
      return;
    }

    const start = input.selectionStart ?? messageText.length;
    const end = input.selectionEnd ?? messageText.length;

    const newText = messageText.slice(0, start) + emoji + messageText.slice(end);
    setMessageText(newText);

    requestAnimationFrame(() => {
      const nextPos = start + emoji.length;
      input.focus();
      input.setSelectionRange(nextPos, nextPos);
    });
  };

  const handlePinMessage = async (message: Message) => {
    const isPinned = message.flags?.pin || false;
    
    try {
      await api.post(`/api/messages/${message.id}/pin`, { value: !isPinned });
      await loadMessages();
    } catch (error) {
      console.error('Ошибка закрепления:', error);
      alert('Ошибка закрепления сообщения');
    }
  };

  const handleDeleteMessage = async (messageId: number) => {
    try {
      await api.delete(`/api/messages/${messageId}`);
      await loadMessages();
    } catch (error) {
      console.error('Ошибка удаления:', error);
      alert('Ошибка удаления сообщения');
    }
  };

  const handleQuoteMessage = (message: Message) => {
    const quotedText = message.text || '[без текста]';
    const isOperator = isOperatorMessage(message);
    const senderName = isOperator 
      ? (message.operator_pseudonym || message.sender_name || 'Оператор')
      : (message.sender_name || 'Пользователь');
    const quoteMarkup = `> **${senderName}:** ${quotedText}\n\n`;
    setMessageText(prev => quoteMarkup + prev);
    messageInputRef.current?.focus();
  };

  if (!chat) {
    return (
      <div className="messages-empty">
        <i className="bi bi-chat-dots"></i>
        <h5>Выберите чат для начала работы</h5>
        <p className="messages-empty-hint-desktop">Используйте список слева</p>
        <p className="messages-empty-hint-mobile">Выберите чат из списка выше</p>
      </div>
    );
  }

  const chatStatus = chat.status || (chat.is_active === false ? 'closed' : 'open');
  const canResumeClosedChat = Boolean(chat.chat_metadata?.has_new_message_after_resolve);

  const handleResumeChat = async () => {
    if (!chat) return;

    try {
      await api.post(`/api/chats/${chat.id}/resume`);
      await loadMessages();
      onMessageSent();
    } catch (error) {
      console.error('Ошибка возобновления обращения:', error);
      const detail = (error as any)?.response?.data?.detail;
      alert(detail || 'Ошибка возобновления обращения');
    }
  };
  
  // Helper function to get avatar URL
  const getAvatarUrl = (): string | null => {
    if (!chat.avatar_id) return null;
    
    const avatarId = String(chat.avatar_id);
    if (avatarId.startsWith('http://') || avatarId.startsWith('https://')) {
      return `/api/avatar?avatar_id=${encodeURIComponent(avatarId)}`;
    }
    return `/api/avatar/${avatarId}`;
  };

  const displayName = chat.display_name || chat.full_name || chat.first_name || chat.nickname || `User ${chat.chat_id}`;
  const firstLetter = displayName[0]?.toUpperCase() || 'U';
  const avatarUrl = getAvatarUrl();
  const getChatChannel = (): 'MAX' | 'TG' | null => {
    if (!chat?.chat_id) return null;
    const chatId = String(chat.chat_id).toLowerCase();
    if (chatId.startsWith('max_')) return 'MAX';
    if (chatId.startsWith('telegram_')) return 'TG';
    if (/^\d+$/.test(chatId)) return 'TG';
    return null;
  };
  const channel = getChatChannel();
  const searchQuery = messageSearch.trim().toLowerCase();
  const filteredMessages = searchQuery
    ? messages.filter((m) => (m.text || '').toLowerCase().includes(searchQuery))
    : messages;

  const urlRegex = /((?:https?:\/\/|www\.)[^\s<>")\]]+)/gi;
  const urlTestRegex = /^(?:https?:\/\/|www\.)[^\s<>")\]]+$/i;

  const normalizeUrl = (raw: string) => {
    if (!raw) return raw;
    const trimmed = raw.trim();
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  const isRenderableUrl = (raw: string) => {
    if (!raw) return false;
    const normalized = normalizeUrl(raw);
    if (!/^https?:\/\//i.test(normalized)) return false;

    try {
      const parsedUrl = new URL(normalized);
      const hostname = parsedUrl.hostname.toLowerCase();
      if (!hostname || hostname === 'localhost' || hostname.endsWith('.local')) {
        return false;
      }
      if (!hostname.includes('.')) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  };

  const isPreviewableUrl = (url: string) => {
    return isRenderableUrl(url);
  };

  const renderMessageText = (text: string) => {
    if (!text) return null;
    const parts = text.split(urlRegex);
    return (
      <>
        {parts.map((part, idx) => {
          if (urlTestRegex.test(part)) {
            if (!isRenderableUrl(part)) {
              return <React.Fragment key={`txt-url-${idx}`}>{part}</React.Fragment>;
            }
            const href = normalizeUrl(part);
            return (
              <a
                key={`url-${idx}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="message-link"
              >
                {part}
              </a>
            );
          }
          return <React.Fragment key={`txt-${idx}`}>{part}</React.Fragment>;
        })}
      </>
    );
  };

  const getFirstUrl = (text: string): string | null => {
    if (!text) return null;
    const matches = text.match(urlRegex) || [];
    const firstRenderable = matches.find((match) => isRenderableUrl(match));
    return firstRenderable ? normalizeUrl(firstRenderable) : null;
  };

  const renderLinkPreview = (text: string) => {
    const url = getFirstUrl(text);
    if (!url || !isPreviewableUrl(url)) return null;

    return <LinkPreviewCard url={url} />;
  };

  return (
    <div
      className={`messages-container-wrapper ${showInfo ? 'with-info' : 'no-info'}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
    >
      {isDragOver && chatStatus === 'open' && (
        <div
          onDrop={handleOverlayDrop}
          onDragOver={handleDragOver}
          style={{
            position: 'absolute', inset: 0, zIndex: 1000,
            background: 'rgba(99,102,241,0.15)',
            border: '3px dashed #6366f1',
            borderRadius: '12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'all',
          }}
        >
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '24px 40px', textAlign: 'center',
            boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📎</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#6366f1' }}>
              Перетащите файл сюда
            </div>
            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
              до 20 МБ
            </div>
          </div>
        </div>
      )}
      {filePreview && (
        <div className="file-preview-overlay" onClick={() => setFilePreview(null)}>
          <div className="file-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="file-preview-header">
              <div className="file-preview-title">{filePreview.fileName}</div>
              <button type="button" className="file-preview-close" onClick={() => setFilePreview(null)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="file-preview-body">
              <img src={filePreview.url} alt={filePreview.fileName} />
            </div>
          </div>
        </div>
      )}

      {/* Контекстное меню */}
      {contextMenu && (
        <MessageContextMenu
          message={contextMenu.message}
          isOperator={isOperatorMessage(contextMenu.message)}
          x={contextMenu.x}
          y={contextMenu.y}
          onReply={() => handleReplyToMessage(contextMenu.message)}
          onEdit={() => handleEditMessage(contextMenu.message)}
          onPin={() => handlePinMessage(contextMenu.message)}
          onDelete={() => handleDeleteMessage(contextMenu.message.id)}
          onQuote={() => handleQuoteMessage(contextMenu.message)}
          onClose={() => setContextMenu(null)}
        />
      )}
      {/* Заголовок */}
      <div className="messages-header">
        <div className="messages-header-left">
          <button className="header-back-btn" onClick={onBackToList} title="Назад к списку">
            <i className="bi bi-arrow-left"></i>
          </button>
          <div className="header-avatar-container">
            <div className="header-avatar">
              <div className="info-avatar-fallback" style={{ fontSize: '1.125rem' }}>{firstLetter}</div>
              {avatarUrl ? (
                <img 
                  src={avatarUrl} 
                  alt={displayName}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                  style={{width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%'}}
                />
              ) : null}
            </div>
            <span className="header-avatar-status"></span>
          </div>
          <div className="header-chat-info">
            <h6>
              {displayName}
              {channel && (
                <span className={`channel-badge header-channel-badge ${channel === 'MAX' ? 'channel-badge-max' : 'channel-badge-tg'}`}>
                  {channel}
                </span>
              )}
            </h6>
            <small className="header-chat-status">был(а) недавно</small>
          </div>
        </div>
        <div className="messages-header-right">
          <div className="header-search-container">
            <input 
              type="text" 
              className="header-search" 
              placeholder="Поиск по сообщениям"
              value={messageSearch}
              onChange={(e) => setMessageSearch(e.target.value)}
            />
            <i className="bi bi-search header-search-icon"></i>
          </div>
          <select
            value={chatLang}
            onChange={(e) => handleSetChatLang(e.target.value)}
            title="Язык перевода ответов"
            style={{ fontSize: '12px', padding: '3px 6px', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff', cursor: 'pointer', color: chatLang === 'auto' ? '#6b7280' : '#6366f1' }}
          >
            <option value="auto">🌐 Авто</option>
            <option value="ru">🇷🇺 Русский</option>
            <option value="en">🇬🇧 English</option>
            <option value="uk">🇺🇦 Українська</option>
            <option value="de">🇩🇪 Deutsch</option>
            <option value="fr">🇫🇷 Français</option>
            <option value="es">🇪🇸 Español</option>
            <option value="kk">🇰🇿 Қазақ</option>
          </select>
          <button className="header-btn" onClick={onToggleInfo} title="Информация">
            <i className="bi bi-info-circle"></i>
          </button>
          <button className="header-btn" title="Меню">
            <i className="bi bi-three-dots-vertical"></i>
          </button>
        </div>
      </div>

      {/* Сообщения */}
      <div className="messages-list-container" ref={messagesContainerRef}>
        {/* Закреплённые сообщения - показываем всегда, когда чат открыт */}
        <div className="pinned-messages-box">
          <div className="pinned-messages-title">
            <i className="bi bi-pin-angle-fill"></i>
            <span>Закреплённые сообщения ({pinnedMessages.length})</span>
          </div>
          <div className="pinned-messages-list">
            {pinnedMessages.length > 0 ? (
              pinnedMessages.map((pm) => (
                <div 
                  key={`pinned-${pm.id}`} 
                  className="pinned-message-item"
                  onClick={() => scrollToMessage(pm.id)}
                  title="Нажмите для перехода к сообщению"
                  style={{ cursor: 'pointer' }}
                >
                  {(pm.text || '[без текста]').slice(0, 120)}
                </div>
              ))
            ) : (
              <div className="pinned-message-empty">Нет закреплённых сообщений в этом чате</div>
            )}
          </div>
        </div>

        {/* Список сообщений */}
        {loading ? (
          <div className="messages-loading">Загрузка сообщений...</div>
        ) : messages.length === 0 ? (
              <div className="no-messages">Нет сообщений</div>
            ) : (
              <div className="messages-list">
                {filteredMessages.length === 0 && (
                  <div className="no-messages">По вашему запросу ничего не найдено</div>
                )}

                {filteredMessages.map((message, index) => {
              const isOperator = isOperatorMessage(message);
              const isHighlighted = highlightedMessageId === message.id;
              const replyPreview = getReplyPreview(message.reply_to);
              const senderName = getMessageSenderName(message);
              const deliveryMarker = getDeliveryMarker(message, Boolean(isOperator));
              const hasMessageText = Boolean((message.text || '').trim());
              // RU для входящего: сперва персист (original_text), иначе эфемерный перевод
              const incomingRu = !isOperator
                ? ((message.original_text && message.original_text !== message.text)
                    ? message.original_text
                    : translations[message.id])
                : undefined;

              return (
                <div
                  id={`message-${message.id}`}
                  key={message.id || index}
                  className={`message ${isOperator ? 'message-operator' : 'message-user'} ${isHighlighted ? 'message-highlighted' : ''}`}
                  style={isHighlighted ? { backgroundColor: 'rgba(255, 193, 7, 0.2)', borderRadius: '8px', padding: '4px' } : {}}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({message, x: e.clientX, y: e.clientY});
                  }}
                  onDoubleClick={() => handleReplyToMessage(message)}
                >
                  <div className="message-header">
                    <span className="message-sender">
                      {isOperator && '👨‍💼 '}
                      {!isOperator && '👤 '}
                      {senderName}
                      {message.is_edited && <span className="ml-2 text-[10px] text-gray-400">(ред.)</span>}
                    </span>
                    <span className="message-time-row">
                      <span className="message-time">
                        {new Date(message.created_at).toLocaleString('ru-RU', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </span>
                      {deliveryMarker && (
                        <span className={`message-delivery ${deliveryMarker.className}`} title={deliveryMarker.title}>
                          <i className={`bi ${deliveryMarker.icon}`}></i>
                        </span>
                      )}
                    </span>
                  </div>
                  {message.reply_to && (
                    <div
                      className={`message-reply-quote ${isOperator ? 'message-reply-quote-operator' : 'message-reply-quote-user'}`}
                      onClick={() => message.reply_to && scrollToMessage(message.reply_to)}
                      title="Перейти к исходному сообщению"
                    >
                      <i className="bi bi-arrow-return-right"></i>
                      <div className="reply-quote-body">
                        <div className="reply-quote-sender">{replyPreview.sender}</div>
                        <div className="reply-quote-text">{replyPreview.text}</div>
                      </div>
                    </div>
                  )}
                  {hasMessageText && (
                    <div className="message-content">
                      {(!isOperator && incomingRu) ? (
                        <>
                          {/* RU-перевод — основной текст */}
                          {renderMessageText(incomingRu)}
                          {message.is_edited && <span className="ml-2 text-[10px] text-gray-400">(ред.)</span>}
                          {/* Оригинал — свёрнут */}
                          <div style={{ marginTop: '3px' }}>
                            <button
                              onClick={() => setShowOriginal(prev => ({ ...prev, [message.id]: !prev[message.id] }))}
                              style={{ fontSize: '11px', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: '0' }}
                            >
                              {showOriginal[message.id] ? '▾ приховати оригінал' : '▸ оригінал'}
                            </button>
                            {showOriginal[message.id] && (
                              <div style={{ marginTop: '2px', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                                {message.text}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (isOperator && message.original_text && message.original_text !== message.text) ? (
                        <>
                          {/* Исходящее с переводом: RU оператора — основной, отправленный (uk) — свёрнут */}
                          {renderMessageText(message.original_text)}
                          {message.is_edited && <span className="ml-2 text-[10px] text-gray-400">(ред.)</span>}
                          <div style={{ marginTop: '3px' }}>
                            <button
                              onClick={() => setShowOriginal(prev => ({ ...prev, [message.id]: !prev[message.id] }))}
                              style={{ fontSize: '11px', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: '0' }}
                            >
                              {showOriginal[message.id] ? '▾ скрыть отправленное' : '▸ отправлено клиенту'}
                            </button>
                            {showOriginal[message.id] && (
                              <div style={{ marginTop: '2px', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                                {message.text}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {renderMessageText(message.text)}
                          {message.is_edited && <span className="ml-2 text-[10px] text-gray-400">(ред.)</span>}
                          {!isOperator && (message.text || '').trim().length > 0 && (
                            <div style={{ marginTop: '4px' }}>
                              <button
                                onClick={() => handleTranslate(message.id, message.text || '')}
                                style={{ fontSize: '11px', color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', padding: '0' }}
                                disabled={translating[message.id]}
                              >
                                {translating[message.id] ? '⏳' : '🌐 Перевести сообщение'}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {renderLinkPreview(message.text)}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className="message-attachments mt-2">
                      {message.attachments.map((att: any, i: number) => {
                        const downloadUrl = att.download_url || att.file_path;
                        const mime = String(att.mime || '').toLowerCase();
                        const name = String(att.file_name || '');
                        const isVoiceAttachment = att.type === 'voice' || mime.startsWith('audio/');
                        const isVideoNoteAttachment =
                          att.type === 'video_note' ||
                          (
                            (att.type === 'video' || mime.startsWith('video/')) &&
                            Number(att.width) > 0 &&
                            Number(att.height) > 0 &&
                            Number(att.width) === Number(att.height)
                          );
                        const isImageAttachment =
                          att.type === 'photo' ||
                          mime.startsWith('image/') ||
                          /\.(png|jpe?g|gif|webp|svg)$/i.test(name);
                        const canInlinePreview = Boolean(
                          downloadUrl && (
                            (typeof att.hash === 'string' && att.hash.length === 64) ||
                            String(downloadUrl).startsWith('http://') ||
                            String(downloadUrl).startsWith('https://') ||
                            String(downloadUrl).startsWith('/api/files/') ||
                            String(downloadUrl).startsWith('api/files/')
                          )
                        );

                        if (isVoiceAttachment) {
                          const attKey = att.file_id || `${message.id}-${i}`;
                          return (
                            <div key={i} className="attachment-card attachment-card-voice mt-2">
                              <div className="tg-voice-bubble">
                                <audio
                                  controls
                                  preload="metadata"
                                  className="tg-voice-player"
                                  src={downloadUrl ? String(downloadUrl) : undefined}
                                />
                                <div className="tg-voice-meta">
                                  <span className="tg-voice-name">{att.file_name || 'Голосовое сообщение'}</span>
                                  <span className="tg-voice-size">{formatFileSize(att.file_size) || 'audio'}</span>
                                  {(att.file_id || att.hash) && (
                                    <button
                                      onClick={() => transcriptions[attKey]
                                        ? setTranscriptions(prev => { const n = {...prev}; delete n[attKey]; return n; })
                                        : handleTranscribe(attKey, att)}
                                      disabled={transcribing[attKey]}
                                      style={{ marginLeft: '8px', fontSize: '11px', color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', padding: '0' }}
                                      title="Расшифровать в текст"
                                    >
                                      {transcribing[attKey] ? '⏳' : transcriptions[attKey] ? '✕' : '📝'}
                                    </button>
                                  )}
                                </div>
                              </div>
                              {transcriptions[attKey] && (
                                <div style={{ marginTop: '6px', padding: '8px 10px', background: '#f0f4ff', borderRadius: '8px', fontSize: '13px', color: '#374151', borderLeft: '3px solid #6366f1' }}>
                                  📝 {transcriptions[attKey]}
                                </div>
                              )}
                            </div>
                          );
                        }

                        if (isVideoNoteAttachment) {
                          return (
                            <div key={i} className="attachment-card attachment-card-video-note mt-2">
                              <div className="tg-video-note-wrap">
                                <video
                                  controls
                                  preload="metadata"
                                  className="tg-video-note-player"
                                  src={downloadUrl ? String(downloadUrl) : undefined}
                                />
                                <div className="tg-video-note-meta">
                                  <span className="tg-video-note-title">Видеосообщение</span>
                                  <span className="tg-video-note-size">{formatFileSize(att.file_size) || 'video_note'}</span>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        if (isImageAttachment) {
                          return (
                            <div key={i} className="attachment-card attachment-card-image mt-2">
                              <button
                                type="button"
                                className="attachment-thumb-btn"
                                onClick={() => {
                                  if (!canInlinePreview || !downloadUrl) return;
                                  setFilePreview({
                                    url: String(downloadUrl),
                                    fileName: att.file_name || 'photo'
                                  });
                                }}
                                disabled={!canInlinePreview || !downloadUrl}
                              >
                                <div className="attachment-thumb-wrap">
                                  <img
                                    src={canInlinePreview && downloadUrl ? String(downloadUrl) : '#'}
                                    className="attachment-thumb"
                                    alt={att.file_name || 'photo'}
                                  />
                                  <span className="attachment-eye">
                                    <i className="bi bi-eye-fill"></i>
                                  </span>
                                </div>
                              </button>
                              <div className="attachment-meta">
                                <div className="attachment-name">{att.file_name || 'Фото'}</div>
                                <div className="attachment-size">{formatFileSize(att.file_size) || 'Изображение'}</div>
                              </div>
                            </div>
                          );
                        }
                        // Для не-изображений создаем красивую карточку файла как в Telegram
                        const fileIcon = getFileIcon(att.file_name || '', att.mime || '', att.type || '');
                        const fileIconColor = getFileIconColor(att.file_name || '', att.mime || '', att.type || '');
                        const fileSize = formatFileSize(att.file_size);
                        
                        return (
                          <div key={i} className="attachment-card attachment-card-file mt-2">
                            <div className="file-card">
                              <div className="file-icon-container" style={{ backgroundColor: `${fileIconColor}15` }}>
                                <i className={`bi ${fileIcon}`} style={{ color: fileIconColor, fontSize: '1.5rem' }}></i>
                              </div>
                              <div className="file-info">
                                <div className="file-name">{att.file_name || 'Файл'}</div>
                                <div className="file-details">
                                  <span className="file-size">{fileSize}</span>
                                  {att.type && att.type !== 'document' && (
                                    <span className="file-type">• {att.type === 'voice' ? 'Голосовое сообщение' : att.type}</span>
                                  )}
                                </div>
                              </div>
                              <a 
                                href={downloadUrl ? `${downloadUrl}?download=1` : '#'} 
                                className="file-download-btn"
                                target="_blank"
                                rel="noopener noreferrer"
                                download={att.file_name || 'file'}
                                title="Скачать"
                              >
                                <i className="bi bi-download"></i>
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="message-actions">
                    <button
                      type="button"
                      className="message-menu-trigger"
                      title="Действия с сообщением"
                      onClick={(e) => {
                        const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                        setContextMenu({
                          message,
                          x: Math.round(rect.left),
                          y: Math.round(rect.bottom + 6)
                        });
                      }}
                    >
                      <i className="bi bi-three-dots"></i>
                    </button>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
              </div>
            )}
      </div>

      {/* Ввод сообщения */}
      {chatStatus === 'open' ? (
        <div className="message-input-container">
          {editingMessageId && (
            <div className="reply-preview-bar edit-preview-bar">
              <div className="reply-preview-content">
                <i className="bi bi-pencil-square"></i>
                <div>
                  <div className="reply-preview-title">Редактирование сообщения</div>
                  <div className="reply-preview-text">
                    {getEditingPreviewText()}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="reply-preview-close"
                title="Отменить редактирование"
                onClick={handleCancelEdit}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          )}
          {replyToMessage && (
            <div className="reply-preview-bar">
              <div className="reply-preview-content">
                <i className="bi bi-arrow-return-right"></i>
                <div>
                  <div className="reply-preview-title">{getReplyPreview(replyToMessage.id).sender}</div>
                  <div className="reply-preview-text">
                    {getReplyPreview(replyToMessage.id).text}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="reply-preview-close"
                title="Отменить ответ"
                onClick={cancelReply}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          )}
          <input 
            type="file" 
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: 'none' }}
          />
          {spellNotice && (
            <div
              style={{
                margin: '0 8px 6px 8px',
                padding: '6px 10px',
                fontSize: '12px',
                background: '#e8f5e9',
                border: '1px solid #c8e6c9',
                borderRadius: '6px',
                color: '#1b5e20',
                whiteSpace: 'pre-line',
                lineHeight: 1.4,
              }}
            >
              <i className="bi bi-magic" style={{ marginRight: 6 }} />
              {spellNotice}
            </div>
          )}
          <form className="message-input-form" onSubmit={handleSendMessage}>
            <button 
              type="button" 
              className="input-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <i className="bi bi-paperclip"></i>
            </button>
            <button
              type="button"
              className="input-attach-btn"
              style={{ opacity: autoSpellOn ? 1 : 0.45 }}
              onClick={() => {
                const next = !autoSpellOn;
                setAutoSpellOn(next);
                localStorage.setItem('autoSpell', next ? 'on' : 'off');
              }}
              title={autoSpellOn ? 'Авто-орфография ВКЛ (Gemini) — клик чтобы выключить' : 'Авто-орфография ВЫКЛ — клик чтобы включить'}
            >
              <i className="bi bi-spellcheck"></i>
            </button>
            <div className="emoji-picker-wrapper" ref={emojiPickerRef}>
              <button
                type="button"
                className="input-emoji-btn"
                onClick={() => setShowEmojiPicker(prev => !prev)}
                title="Эмодзи"
              >
                <i className="bi bi-emoji-smile"></i>
              </button>
              {showEmojiPicker && (
                <div className="emoji-picker-popover">
                  <div className="emoji-picker-grid">
                    {quickEmojis.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        className="emoji-item"
                        onClick={() => insertEmoji(emoji)}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="input-textarea-wrapper">
              <textarea
                ref={messageInputRef}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="message-textarea"
                rows={2}
                placeholder={editingMessageId ? 'Редактировать сообщение...' : 'Введите сообщение...'}
                disabled={sending}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
              />
            </div>
            <button
              type="submit"
              className="input-send-btn"
              disabled={sending || !messageText.trim()}
              title={editingMessageId ? 'Сохранить изменения' : 'Отправить сообщение'}
            >
              <i className={`bi ${editingMessageId ? 'bi-check2' : 'bi-send'}`}></i>
            </button>
          </form>
        </div>
      ) : (
        <div className="chat-closed-notice">
          Чат закрыт. Отправка сообщений недоступна.
          {canResumeClosedChat && (
            <div style={{ marginTop: 10 }}>
              <button type="button" className="info-success-btn" onClick={handleResumeChat}>
                Продолжить
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// GitLab issue (см. backend: api/gitlab.py — GitlabIssue.to_dict)
type GitlabIssue = {
  id: number; iid: number; web_url: string; title: string; state: string;
  issue_type?: string; labels: string[]; has_unseen_update?: boolean; last_polled_at?: string | null;
};

// ОТКРЫТЫЕ issue (state=opened) — сверху; внутри группы по убыванию iid.
const sortGitlabIssues = (arr: GitlabIssue[]): GitlabIssue[] =>
  [...arr].sort((a, b) => {
    const ao = a.state === 'opened' ? 0 : 1;
    const bo = b.state === 'opened' ? 0 : 1;
    if (ao !== bo) return ao - bo;
    return (b.iid || 0) - (a.iid || 0);
  });

// Правая панель с информацией
const InfoPanel: React.FC<{
  chat: Chat | null;
  onClose: () => void;
  onChatUpdate: () => void;
  currentGroup: number | null;
  onSelectChatChanged?: (chat: Chat | null) => void;
  onChatPatched?: (chat: Chat) => void;
  onShowInfoChanged?: (show: boolean) => void;
  onJumpToMessage?: (messageId: number) => void;
  onInsertText?: (text: string) => void;
}> = ({ chat, onClose, onChatUpdate, currentGroup, onSelectChatChanged, onChatPatched, onShowInfoChanged, onJumpToMessage, onInsertText }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [avatarError, setAvatarError] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [newAlias, setNewAlias] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [companyInn, setCompanyInn] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [tickets, setTickets] = useState('');
  const [comment, setComment] = useState('');

  // RAG: похожие решённые обращения + правка категории оператором
  const [similar, setSimilar] = useState<any[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [wikiArticles, setWikiArticles] = useState<any[]>([]);
  const [wikiLoading, setWikiLoading] = useState(false);
  const [expandedCase, setExpandedCase] = useState<number | null>(null);
  const [savingCat, setSavingCat] = useState(false);

  useEffect(() => {
    setSimilar([]); setExpandedCase(null);
    if (!chat?.id) return;
    let alive = true;
    setSimilarLoading(true);
setWikiLoading(true);
api.get(`/api/chats/${chat.id}/wiki-articles`, { params: { k: 4 } })
  .then(r => setWikiArticles(r.data || []))
  .catch(() => setWikiArticles([]))
  .finally(() => setWikiLoading(false));
    api.get(`/api/chats/${chat.id}/similar-solved`, { params: { k: 3 } })
      .then(r => { if (alive) setSimilar(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setSimilar([]); })
      .finally(() => { if (alive) setSimilarLoading(false); });
    return () => { alive = false; };
  }, [chat?.id]);

  const changeCategory = async (category: string) => {
    if (!chat?.id) return;
    setSavingCat(true);
    try {
      await api.post(`/api/chats/${chat.id}/category`, { category });
      onChatUpdate();
    } catch (e) { console.error('set category failed', e); }
    finally { setSavingCat(false); }
  };

  // AI-разбор обращения (см. backend: POST /api/chats/{chat_id}/ai-analysis)
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [aiTicketNumber, setAiTicketNumber] = useState<number | null>(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiCopied, setAiCopied] = useState(false);

  const requestAiAnalysis = async () => {
    if (!chat?.id) return;
    setAiLoading(true);
    setAiError(null);
    setAiCopied(false);
    setShowAiModal(true);
    try {
      const r = await api.post(`/api/chats/${chat.id}/ai-analysis`);
      setAiAnalysis(r.data?.analysis || '');
      setAiTicketNumber(r.data?.ticket_number ?? null);
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg = 'Не удалось получить разбор';
      if (status === 422) msg = 'Не удалось получить разбор: недостаточно текста в обращении';
      else if (status === 502) msg = 'Не удалось получить разбор: ошибка AI-сервиса, попробуйте ещё раз';
      else if (typeof detail === 'string') msg = `Не удалось получить разбор: ${detail}`;
      else if (e?.message) msg = `Не удалось получить разбор: ${e.message}`;
      setAiError(msg);
      setAiAnalysis(null);
    } finally {
      setAiLoading(false);
    }
  };

  const copyAiAnalysis = async () => {
    if (!aiAnalysis) return;
    try {
      await navigator.clipboard.writeText(aiAnalysis);
      setAiCopied(true);
      setTimeout(() => setAiCopied(false), 1800);
    } catch {
      // fallback для контекстов без clipboard API
      const ta = document.createElement('textarea');
      ta.value = aiAnalysis;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setAiCopied(true); setTimeout(() => setAiCopied(false), 1800); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
  };

  // GitLab integration (см. backend: api/gitlab.py). Тип и сортировка — на уровне модуля (ниже).
  const [gitlabIssues, setGitlabIssues] = useState<GitlabIssue[]>([]);
  // Открытая задача чата: пока она есть, состоянием обращения управляет GitLab.
  const openGitlabIssue = gitlabIssues.find(i => i.state === 'opened');
  const [showGitlabModal, setShowGitlabModal] = useState(false);
  const [glTitle, setGlTitle] = useState('');
  const [glDescription, setGlDescription] = useState('');
  const [glLabels, setGlLabels] = useState<string[]>([]);
  const [glAssigneeId, setGlAssigneeId] = useState<number | ''>('');
  const [glMilestoneId, setGlMilestoneId] = useState<number | ''>('');
  const [glDueDate, setGlDueDate] = useState<string>('');
  const [glParentIid, setGlParentIid] = useState<number | ''>('');
  const [glTimeEstimate, setGlTimeEstimate] = useState<string>('');
  const [glIssueType, setGlIssueType] = useState<'issue' | 'task' | 'incident' | 'test_case'>('issue');
  type GlMember = { id: number; username?: string; name?: string };
  type GlMilestone = { id: number; title: string; due_date?: string | null };
  const [glMembers, setGlMembers] = useState<GlMember[]>([]);
  const [glMilestones, setGlMilestones] = useState<GlMilestone[]>([]);
  const [glSubmitting, setGlSubmitting] = useState(false);
  const [glError, setGlError] = useState<string | null>(null);
  // Режим редактирования: id mbbot-записи issue (null = создание нового)
  const [glEditingId, setGlEditingId] = useState<number | null>(null);
  const [glEditingIid, setGlEditingIid] = useState<number | null>(null);
  // Состояние редактируемого issue (для кнопки «Закрыть»/«Переоткрыть»)
  const [glEditingState, setGlEditingState] = useState<string>('opened');

  useEffect(() => {
    loadUsers();
    loadGroups();
  }, []);

  useEffect(() => {
    if (chat) {
      setAvatarError(false);
      setNewAlias(chat.alias || '');
      setSelectedGroupId(chat.group_id ?? null);
      setCompanyName(chat.company_name || '');
      setCompanyInn(chat.company_inn || '');
      setLicenseNumber(chat.license_number || '');
      setPhone(chat.phone || '');
      setTickets(chat.tickets || '');
      setComment(chat.comment || '');
      // GitLab linked issues for this chat
      api.get(`/api/chats/${chat.id}/gitlab-issues`)
        .then(r => setGitlabIssues(sortGitlabIssues(r.data || [])))
        .catch(() => setGitlabIssues([]));
    }
  }, [chat]);

  const openGitlabModal = async () => {
    if (!chat) return;
    setGlError(null);
    setGlEditingId(null); setGlEditingIid(null);
    setShowGitlabModal(true);
    // Сбрасываем дополнительные поля при каждом открытии
    setGlAssigneeId(''); setGlMilestoneId(''); setGlDueDate(''); setGlParentIid(''); setGlTimeEstimate('');
    setGlIssueType('issue');
    try {
      const [r, meta] = await Promise.all([
        api.get(`/api/chats/${chat.id}/gitlab-prefill`),
        api.get('/api/gitlab/meta').catch(() => ({ data: { members: [], milestones: [] } })),
      ]);
      setGlTitle(r.data.title || '');
      setGlDescription(r.data.description || '');
      setGlLabels(r.data.labels || []);
      setGlMembers(meta.data.members || []);
      setGlMilestones(meta.data.milestones || []);
    } catch (e: any) {
      setGlError(e?.response?.data?.detail || 'Не удалось получить prefill');
    }
  };

  const openGitlabEditModal = async (gi: GitlabIssue) => {
    setGlError(null);
    setGlEditingId(gi.id); setGlEditingIid(gi.iid);
    setGlEditingState(gi.state || 'opened');
    setShowGitlabModal(true);
    // Поля только для создания — в режиме редактирования не используются
    setGlParentIid(''); setGlTimeEstimate('');
    // Мгновенный prefill из уже известных полей, остальное подтянем из /detail
    setGlTitle(gi.title || '');
    setGlLabels(gi.labels || []);
    setGlIssueType((gi.issue_type as any) || 'issue');
    setGlDescription(''); setGlAssigneeId(''); setGlMilestoneId(''); setGlDueDate('');
    try {
      const [detail, meta] = await Promise.all([
        api.get(`/api/gitlab-issues/${gi.id}/detail`),
        api.get('/api/gitlab/meta').catch(() => ({ data: { members: [], milestones: [] } })),
      ]);
      const d = detail.data;
      setGlTitle(d.title || '');
      setGlDescription(d.description || '');
      setGlLabels(d.labels || []);
      setGlAssigneeId(d.assignee_id ? Number(d.assignee_id) : '');
      setGlMilestoneId(d.milestone_id ? Number(d.milestone_id) : '');
      setGlDueDate(d.due_date || '');
      setGlIssueType((d.issue_type as any) || 'issue');
      setGlMembers(meta.data.members || []);
      setGlMilestones(meta.data.milestones || []);
    } catch (e: any) {
      setGlError(e?.response?.data?.detail || 'Не удалось загрузить issue для редактирования');
    }
  };

  const submitGitlabIssue = async () => {
    setGlSubmitting(true);
    setGlError(null);
    try {
      if (glEditingId) {
        // Редактирование: форма полная, null = снять значение (исполнитель/milestone/дата)
        const payload: any = {
          title: glTitle,
          description: glDescription,
          labels: glLabels,
          assignee_id: glAssigneeId ? Number(glAssigneeId) : null,
          milestone_id: glMilestoneId ? Number(glMilestoneId) : null,
          due_date: glDueDate || null,
          issue_type: glIssueType,
        };
        const r = await api.put(`/api/gitlab-issues/${glEditingId}`, payload);
        setGitlabIssues(prev => sortGitlabIssues(prev.map(i => i.id === glEditingId ? r.data : i)));
        setShowGitlabModal(false);
        return;
      }
      if (!chat) return;
      const payload: any = { title: glTitle, description: glDescription, labels: glLabels };
      if (glAssigneeId) payload.assignee_id = Number(glAssigneeId);
      if (glMilestoneId) payload.milestone_id = Number(glMilestoneId);
      if (glDueDate) payload.due_date = glDueDate;
      if (glParentIid) payload.parent_iid = Number(glParentIid);
      if (glTimeEstimate.trim()) payload.time_estimate = glTimeEstimate.trim();
      payload.issue_type = glIssueType;
      const r = await api.post(`/api/chats/${chat.id}/gitlab-issue`, payload);
      setGitlabIssues(prev => sortGitlabIssues([r.data, ...prev]));
      setShowGitlabModal(false);
    } catch (e: any) {
      setGlError(e?.response?.data?.detail || (glEditingId ? 'Ошибка сохранения issue' : 'Ошибка создания issue'));
    } finally {
      setGlSubmitting(false);
    }
  };

  // Phase C: закрыть / переоткрыть issue (PUT state_event). issueId — id mbbot-записи.
  const changeGitlabIssueState = async (issueId: number, event: 'close' | 'reopen') => {
    if (event === 'close' && !window.confirm('Закрыть issue в GitLab?')) return;
    setGlSubmitting(true);
    setGlError(null);
    try {
      const r = await api.put(`/api/gitlab-issues/${issueId}`, { state_event: event });
      setGitlabIssues(prev => sortGitlabIssues(prev.map(i => i.id === issueId ? r.data : i)));
      setGlEditingState(r.data?.state || (event === 'close' ? 'closed' : 'opened'));
      setShowGitlabModal(false);
      // Закрытие в панели намеренно доигрывает поллер: он же уведомляет клиента и закрывает
      // обращение. Поэтому в списке задача ещё пару минут будет значиться открытой — говорим
      // об этом прямо, иначе выглядит как «кнопка не сработала».
      if (event === 'close') {
        alert('Задача закрыта в GitLab. Обращение закроется само и клиент получит уведомление в течение пары минут.');
      }
    } catch (e: any) {
      setGlError(e?.response?.data?.detail || (event === 'close' ? 'Не удалось закрыть issue' : 'Не удалось переоткрыть issue'));
    } finally {
      setGlSubmitting(false);
    }
  };

  const refreshGitlabIssue = async (issueId: number) => {
    try {
      const r = await api.post(`/api/gitlab-issues/${issueId}/refresh`);
      setGitlabIssues(prev => sortGitlabIssues(prev.map(i => i.id === issueId ? r.data.issue : i)));
    } catch (e) {
      // молча — refresh не критичен
    }
  };

  const markIssueSeen = async (issueId: number) => {
    try {
      await api.post(`/api/gitlab-issues/${issueId}/seen`);
      setGitlabIssues(prev => prev.map(i => i.id === issueId ? { ...i, has_unseen_update: false } : i));
    } catch (e) { /* ignore */ }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/api/users/operators');
      setUsers(response.data);
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await api.get('/api/groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Ошибка загрузки групп:', error);
    }
  };

  const handleTransferChat = async () => {
    if (!chat || !selectedUserId) return;

    try {
      setShowTransferModal(false);
      // Close the info panel and clear selection FIRST
      if (onShowInfoChanged) {
        onShowInfoChanged(false);
      }
      if (onSelectChatChanged) {
        onSelectChatChanged(null);
      }
      // Then call the API and refresh
      await api.post(`/api/chats/${chat.id}/transfer`, {
        user_id: selectedUserId
      });
      onChatUpdate();
    } catch (error) {
      console.error('Ошибка передачи чата:', error);
      alert('Ошибка передачи чата');
    }
  };

  const handleUpdateAlias = async () => {
    if (!chat) return;

    try {
      await api.post(`/api/chats/${chat.id}/details`, {
        alias: newAlias
      });
      setShowAliasModal(false);
      onChatUpdate();
    } catch (error) {
      console.error('Ошибка обновления псевдонима:', error);
      alert('Ошибка обновления псевдонима');
    }
  };

  const handleAddToGroup = async () => {
    if (!chat || !selectedGroupId) return;

    try {
      setShowGroupModal(false);
      // Clear selection and close panel FIRST
      if (onShowInfoChanged) {
        onShowInfoChanged(false);
      }
      if (onSelectChatChanged) {
        onSelectChatChanged(null);
      }
      // Then call the API
      await api.post(`/api/chats/${chat.id}/details`, {
        group_id: selectedGroupId
      });
      // Finally refresh the list
      if (currentGroup !== null && onChatUpdate) {
        onChatUpdate();
      }
    } catch (error) {
      console.error('Ошибка добавления в группу:', error);
      alert('Ошибка добавления в группу');
    }
  };

  const handleSaveDetails = async () => {
    if (!chat) return;

    try {
      const response = await api.post(`/api/chats/${chat.id}/details`, {
        alias: newAlias,
        group_id: selectedGroupId || null,
        company_name: companyName,
        company_inn: companyInn,
        license_number: licenseNumber,
        phone,
        tickets,
        comment
      });

      const updatedChat = response?.data?.data;
      const selectedGroup = groups.find((group) => group.id === (selectedGroupId || undefined));
      const mergedChat: Chat = {
        ...chat,
        ...updatedChat,
        alias: newAlias,
        group_id: selectedGroupId || undefined,
        group_name: updatedChat?.group_name ?? selectedGroup?.group_name ?? chat.group_name,
        company_name: companyName,
        company_inn: companyInn,
        license_number: licenseNumber,
        phone,
        tickets,
        comment,
      };

      if (updatedChat && onSelectChatChanged) {
        onSelectChatChanged(mergedChat);
      }

      onChatPatched?.(mergedChat);
    } catch (error) {
      console.error('Ошибка сохранения данных чата:', error);
      const detail = (error as any)?.response?.data?.detail;
      alert(detail || 'Ошибка сохранения данных чата');
    }
  };

  const handleCloseChat = async () => {
    if (!chat) return;
    if (!window.confirm('Вы уверены, что хотите закрыть этот чат?')) return;

    try {
      await api.post(`/api/chats/${chat.id}/move`, { trash: true });
      onChatUpdate();
    } catch (error) {
      console.error('Ошибка закрытия чата:', error);
      alert('Ошибка закрытия чата');
    }
  };

  const handleReopenChat = async () => {
    if (!chat) return;

    try {
      if (chat.trash) {
        await api.post(`/api/chats/${chat.id}/move`, { trash: false });
      } else {
        await api.post(`/api/chats/${chat.id}/resume`);
      }
      onChatUpdate();
    } catch (error) {
      console.error('Ошибка открытия чата:', error);
      alert('Ошибка открытия чата');
    }
  };

  const handleSetInWork = async () => {
    if (!chat) return;

    try {
      const currentlyInWork = Boolean(chat.chat_metadata?.in_work);
      await api.post(`/api/chats/${chat.id}/work`, {
        enabled: !currentlyInWork,
      });
      onChatUpdate();
    } catch (error) {
      console.error('Ошибка перевода обращения в работу:', error);
      const detail = (error as any)?.response?.data?.detail;
      alert(detail || 'Ошибка перевода обращения в работу');
    }
  };

  const handleResolveChat = async () => {
    if (!chat) return;

    try {
      await api.post(`/api/chats/${chat.id}/resolve`, {
        notify_user: true,
      });
      onChatUpdate();
    } catch (error) {
      console.error('Ошибка завершения обращения:', error);
      const detail = (error as any)?.response?.data?.detail;
      alert(detail || 'Ошибка завершения обращения');
    }
  };

  if (!chat) {
    return (
      <div className="info-panel-empty">
        <p>Информация о чате</p>
      </div>
    );
  }

  const getInfoAvatarUrl = (): string | null => {
    if (!chat.avatar_id) return null;

    const normalizedAvatarId = String(chat.avatar_id);
    if (normalizedAvatarId.startsWith('http://') || normalizedAvatarId.startsWith('https://')) {
      return `/api/avatar?avatar_id=${encodeURIComponent(normalizedAvatarId)}`;
    }
    return `/api/avatar/${normalizedAvatarId}`;
  };

  const infoAvatarUrl = getInfoAvatarUrl();
  const fallbackLetter = (chat.display_name || chat.full_name || chat.first_name || chat.nickname || 'U')[0]?.toUpperCase() || 'U';
  const getInfoChatChannel = (): 'MAX' | 'TG' | null => {
    if (!chat?.chat_id) return null;
    const chatId = String(chat.chat_id).toLowerCase();
    if (chatId.startsWith('max_')) return 'MAX';
    if (chatId.startsWith('telegram_')) return 'TG';
    if (/^\d+$/.test(chatId)) return 'TG';
    return null;
  };
  const infoChannel = getInfoChatChannel();

  const chatStatus = chat.status || (chat.is_active === false ? 'closed' : 'open');
  const isInWork = Boolean(chat.chat_metadata?.in_work);

  return (
    <div className="info-panel">
      <div className="info-header">
        <h6 className="info-title">Информация</h6>
        <button type="button" onClick={onClose} className="info-close-btn">
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      <div className="info-avatar-section">
        <div className="info-avatar">
          {infoAvatarUrl && !avatarError ? (
            <img
              src={infoAvatarUrl}
              alt={chat.display_name || chat.full_name || chat.first_name || chat.nickname || `User ${chat.chat_id}`}
              className="info-avatar-image"
              onError={() => setAvatarError(true)}
            />
          ) : (
            <div className="info-avatar-fallback">{fallbackLetter}</div>
          )}
        </div>
        <h6 className="info-name">
          {chat.display_name || chat.full_name || chat.first_name || chat.nickname || `User ${chat.chat_id}`}
          {infoChannel && (
            <span className={`channel-badge info-channel-badge ${infoChannel === 'MAX' ? 'channel-badge-max' : 'channel-badge-tg'}`}>
              {infoChannel}
            </span>
          )}
        </h6>
        {chat.alias && <small className="info-nickname">@{chat.alias}</small>}
        {chat.group_name && <span className="info-group-badge">{chat.group_name}</span>}
        {isInWork && <span className="info-status-badge info-status-in-work">Статус: В работе</span>}
      </div>

      {/* Категория обращения (правка оператором) */}
      <div className="info-cat-edit">
        <label>🗂 Категория обращения{chat.ticket_category_source === 'ai' ? ' (AI)' : ''}:</label>
        <select
          value={chat.ticket_category || ''}
          disabled={savingCat}
          onChange={(e) => changeCategory(e.target.value)}
        >
          <option value="">— не задана —</option>
          <optgroup label="🐛 Баг">
            <option value="bug:payments">Платежи</option>
            <option value="bug:sorm">СОРМ</option>
            <option value="bug:radius">Radius/сессии</option>
            <option value="bug:ippool">IP-пулы</option>
            <option value="bug:reports">Отчёты</option>
            <option value="bug:lk">Личный кабинет</option>
            <option value="bug:ui">Интерфейс</option>
            <option value="bug:other">Прочее</option>
          </optgroup>
          <optgroup label="🛠 Доработка">
            <option value="feature:integration">Интеграция</option>
            <option value="feature:report">Отчёт</option>
            <option value="feature:lk">Личный кабинет</option>
            <option value="feature:api">API</option>
            <option value="feature:other">Прочее</option>
          </optgroup>
          <optgroup label="📦 Установка/перенос">
            <option value="install:new">Новая установка</option>
            <option value="install:migrate">Миграция ОС</option>
            <option value="install:update">Обновление</option>
          </optgroup>
          <option value="help">🆘 Вопрос/настройка</option>
        </select>
      </div>

      {/* Вики MikBiLL: статьи по теме обращения. Индекс Виктора (гибрид FTS+эмбеддинги),
          ключ на бэкенде. Помеченные «(устарел)» опущены в конец на нашей стороне —
          в самом индексе штраф за устаревшее не срабатывает (проверено 02.09.2026). */}
      {(wikiLoading || wikiArticles.length > 0) && (
        <div className="info-section rag-suggestions">
          <h3>📚 Статьи вики</h3>
          {wikiLoading && <div className="rag-hint">Ищу в вики…</div>}
          {!wikiLoading && wikiArticles.map((w, i) => (
            <div key={i} className="rag-case">
              <div className="rag-case-head">
                <a className="rag-case-title" href={w.url} target="_blank" rel="noreferrer">
                  {w.stale ? '⚠️ ' : ''}{w.title}
                </a>
                <span className="rag-sim">{Math.round((w.score || 0) * 100)}%</span>
              </div>
              {(w.updated || w.stale) && (
                <div className="rag-hint">
                  {w.updated ? `обновлена ${w.updated.split('-').reverse().join('.')}` : ''}
                  {w.updated && w.stale ? ' · ' : ''}
                  {w.stale ? 'помечена устаревшей — проверьте, прежде чем отдавать клиенту' : ''}
                </div>
              )}
              <div className="rag-actions">
                {onInsertText && (
                  <button className="rag-insert" onClick={() => onInsertText(`${w.title}: ${w.url}`)}>
                    ✍️ Вставить ссылку
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* RAG: похожие решённые обращения */}
      {(similarLoading || similar.length > 0) && (
        <div className="info-section rag-suggestions">
          <h3>💡 Похожие решённые</h3>
          {similarLoading && <div className="rag-hint">Поиск похожих…</div>}
          {!similarLoading && similar.map((c, i) => (
            <div key={i} className="rag-case">
              <div className="rag-case-head" onClick={() => setExpandedCase(expandedCase === i ? null : i)}>
                <span className="rag-case-title">{c.company_name || 'Клиент'}{c.category ? ` · ${c.category}` : ''}</span>
                <span className="rag-sim">{Math.round((c.similarity || 0) * 100)}%</span>
              </div>
              <div className="rag-q">❓ {c.question}</div>
              <div className={`rag-a ${expandedCase === i ? 'expanded' : ''}`}>✅ {c.answer}</div>
              <div className="rag-actions">
                <button className="rag-toggle" onClick={() => setExpandedCase(expandedCase === i ? null : i)}>
                  {expandedCase === i ? 'Свернуть' : 'Показать решение'}
                </button>
                {onInsertText && (
                  <button className="rag-insert" onClick={() => onInsertText(c.answer || '')}>
                    ✍️ Вставить в ответ
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="info-actions">
        {chatStatus !== 'open' && !chat.trash && (
          <button className="info-btn" onClick={handleReopenChat}>
            <i className="bi bi-arrow-return-left"></i>
            Продолжить
          </button>
        )}
        {/* 🔴 02.09.2026. Пока по обращению открыта задача в GitLab, обе кнопки состояния
            прячем. «В работу» не нужна: статус «у разработчиков» теперь ставится сам при
            заведении задачи. «Решено» была прямо вредна — она закрывала чат и слала клиенту
            «вопрос решён», не трогая GitLab, а через две минуты сверка возвращала обращение
            в работу (issue-то открыт): клиент прочитал «решено», обращение снова в работе.
            Вместо неё — «Закрыть задачу»: закрывает issue, а обращение закроет и уведомит
            клиента поллер, штатным путём. Закрытие остаётся решением разработчиков. */}
        {chatStatus === 'open' && !openGitlabIssue && (
          <button
            className={`info-btn${isInWork ? ' info-btn-active' : ''}`}
            onClick={handleSetInWork}
          >
            <i className="bi bi-tools"></i>
            {isInWork ? 'В работе' : 'В работу'}
          </button>
        )}
        {chatStatus === 'open' && !openGitlabIssue && (
          <button className="info-btn" onClick={handleResolveChat}>
            <i className="bi bi-check2-circle"></i>
            Решено
          </button>
        )}
        {chatStatus === 'open' && openGitlabIssue && (
          <button
            className="info-btn"
            disabled={glSubmitting}
            title={`Задача #${openGitlabIssue.iid} в GitLab — закрыть её; обращение закроется само`}
            onClick={() => changeGitlabIssueState(openGitlabIssue.id, 'close')}
          >
            <i className="bi bi-check2-circle"></i>
            Закрыть задачу #{openGitlabIssue.iid}
          </button>
        )}
        <button className="info-btn" onClick={() => setShowTransferModal(true)}>
          <i className="bi bi-arrow-left-right"></i>
          Передать
        </button>
        {!chat.trash ? (
          <button className="info-btn info-btn-danger" onClick={handleCloseChat}>
            <i className="bi bi-trash"></i>
            В корзину
          </button>
        ) : (
          <button className="info-btn info-btn-success" onClick={handleReopenChat}>
            <i className="bi bi-arrow-return-left"></i>
            Восстановить
          </button>
        )}
      </div>

      <div className="info-details">
        <div className="info-field">
          <label className="info-label">Псевдоним</label>
          <input 
            type="text" 
            className="info-input" 
            value={newAlias} 
            onChange={(e) => setNewAlias(e.target.value)}
          />
        </div>
        <div className="info-field">
          <label className="info-label">Группа</label>
          <select className="info-select" value={selectedGroupId || ''} onChange={(e) => setSelectedGroupId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">-- Выберите группу --</option>
            {groups.filter(g => {
              const name = (g.group_name || '').toLowerCase();
              return !name.includes('без групп') && !name.includes('корзин');
            }).map(g => (
              <option key={g.id} value={g.id}>{g.group_name}</option>
            ))}
          </select>
        </div>
        <div className="info-field">
          <label className="info-label">Название компании</label>
          <input type="text" className="info-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        </div>
        <div className="info-field">
          <label className="info-label">Лицензия MikBill</label>
          <input
            type="text"
            className="info-input"
            value={licenseNumber}
            onChange={(e) => setLicenseNumber(e.target.value)}
            placeholder="Номер лицензии"
          />
        </div>
        <div className="info-field">
          <label className="info-label">ИНН</label>
          <input type="text" className="info-input" value={companyInn} onChange={(e) => setCompanyInn(e.target.value)} />
        </div>
        <div className="info-field">
          <label className="info-label">Телефон</label>
          <input type="text" className="info-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="info-field">
          <label className="info-label">Тикеты</label>
          <input type="text" className="info-input" value={tickets} onChange={(e) => setTickets(e.target.value)} />
        </div>
        <div className="info-field">
          <label className="info-label">Примечание</label>
          <textarea className="info-textarea" rows={3} value={comment} onChange={(e) => setComment(e.target.value)}></textarea>
        </div>
        <button type="button" className="info-save-btn" onClick={handleSaveDetails}>
          <i className="bi bi-check2"></i>
          Сохранить
        </button>
      </div>

      {/* AI-разбор обращения (см. backend: POST /api/chats/{chat_id}/ai-analysis) */}
      <div className="info-field" style={{ margin: '0 1rem 0.75rem' }}>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={requestAiAnalysis}
                disabled={aiLoading} style={{ width: '100%', fontSize: '0.85rem' }}>
          {aiLoading ? '🤖 Думаю…' : '🤖 Разбор от AI'}
        </button>
      </div>

      {/* GitLab issues — эскалация в gitlab.celteh.net (см. reference_gitlab_celteh) */}
      <div className="info-field" style={{ margin: '0 1rem 0.75rem' }}>
        <label className="info-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>🐞 GitLab issues {gitlabIssues.length > 0 && <span style={{ color: '#888', fontWeight: 400 }}>({gitlabIssues.length})</span>}</span>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={openGitlabModal} style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem' }}>
            <i className="bi bi-plus-lg"></i> Завести
          </button>
        </label>
        {gitlabIssues.length === 0 ? (
          <div style={{ color: '#999', fontSize: '0.85rem' }}>нет связанных issue</div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {gitlabIssues.map(gi => (
              <li key={gi.id} style={{ padding: '0.35rem 0', borderBottom: '1px solid #f0f0f0', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.4rem' }}>
                <span title={gi.issue_type || 'issue'} style={{ fontSize: '0.85rem' }}>
                  {gi.issue_type === 'task' ? '✅' : gi.issue_type === 'incident' ? '🚨' : gi.issue_type === 'test_case' ? '🧪' : '🐞'}
                </span>
                <span style={{
                  display: 'inline-block', padding: '0.1rem 0.4rem', borderRadius: '3px',
                  fontSize: '0.7rem', fontWeight: 600, color: '#fff',
                  background: gi.state === 'opened' ? '#2da44e' : '#8250df',
                }}>{gi.state === 'opened' ? 'OPEN' : 'CLOSED'}</span>
                {gi.has_unseen_update && (
                  <span title="есть новое обновление" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#dc3545' }}
                        onClick={() => markIssueSeen(gi.id)}></span>
                )}
                <a href={gi.web_url} target="_blank" rel="noreferrer" style={{ flex: 1, minWidth: 120, color: '#0969da', fontSize: '0.85rem', textDecoration: 'none' }}
                   title={gi.title}>#{gi.iid} {gi.title.length > 40 ? gi.title.slice(0, 40) + '…' : gi.title}</a>
                <button type="button" title="Редактировать issue" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: '0.8rem' }}
                        onClick={() => openGitlabEditModal(gi)}>
                  <i className="bi bi-pencil"></i>
                </button>
                {gi.state === 'opened' ? (
                  <button type="button" title="Закрыть issue" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: '0.8rem', color: '#8250df' }}
                          onClick={() => changeGitlabIssueState(gi.id, 'close')}>
                    <i className="bi bi-check-circle"></i>
                  </button>
                ) : (
                  <button type="button" title="Переоткрыть issue" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: '0.8rem', color: '#2da44e' }}
                          onClick={() => changeGitlabIssueState(gi.id, 'reopen')}>
                    <i className="bi bi-arrow-counterclockwise"></i>
                  </button>
                )}
                <button type="button" title="Обновить состояние" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: '0.8rem' }}
                        onClick={() => refreshGitlabIssue(gi.id)}>
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
                {gi.labels && gi.labels.length > 0 && (
                  <span style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: '0.25rem', paddingLeft: '1.6rem' }}>
                    {gi.labels.map(lbl => (
                      <span key={lbl} style={{
                        display: 'inline-block', padding: '0.05rem 0.4rem', borderRadius: '10px',
                        fontSize: '0.68rem', background: '#eef1f4', color: '#444', border: '1px solid #dde2e7',
                      }}>{lbl}</span>
                    ))}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* GitLab create modal */}
      {showGitlabModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => !glSubmitting && setShowGitlabModal(false)}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '8px', width: '90%', maxWidth: 720, maxHeight: '90vh', overflow: 'auto' }}
               onClick={(e) => e.stopPropagation()}>
            <h5 style={{ marginTop: 0 }}>{glEditingId ? `✏️ Редактировать issue #${glEditingIid}` : '🐞 Завести issue в GitLab'}</h5>
            <small style={{ color: '#666' }}>gitlab.celteh.net / legacy/mikbill</small>
            {glError && <div style={{ background: '#fee', border: '1px solid #fcc', padding: '0.5rem', borderRadius: 4, margin: '0.5rem 0', color: '#900' }}>{glError}</div>}
            <div style={{ marginTop: '0.75rem' }}>
              <label className="info-label">Тип</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {([
                  { v: 'issue', label: '🐞 Issue', desc: 'обычная задача' },
                  { v: 'task', label: '✅ Task', desc: 'подзадача (часто с родителем)' },
                  { v: 'incident', label: '🚨 Incident', desc: 'инцидент' },
                  { v: 'test_case', label: '🧪 Test case', desc: 'тест-кейс' },
                ] as const).map(opt => (
                  <label key={opt.v} title={opt.desc} style={{
                    display: 'flex', alignItems: 'center', gap: 4, padding: '0.25rem 0.5rem',
                    border: `2px solid ${glIssueType === opt.v ? '#0969da' : '#ddd'}`,
                    borderRadius: 4, cursor: glSubmitting ? 'not-allowed' : 'pointer',
                    background: glIssueType === opt.v ? '#e7f1ff' : '#fff', fontSize: '0.85rem',
                  }}>
                    <input type="radio" name="gl-issue-type" value={opt.v}
                           checked={glIssueType === opt.v}
                           onChange={() => setGlIssueType(opt.v)}
                           disabled={glSubmitting} style={{ margin: 0 }} />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <label className="info-label">Title</label>
              <input type="text" className="info-input" value={glTitle} maxLength={500} onChange={e => setGlTitle(e.target.value)} disabled={glSubmitting} />
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <label className="info-label">Description (markdown)</label>
              <textarea className="info-textarea" rows={14} value={glDescription} onChange={e => setGlDescription(e.target.value)} disabled={glSubmitting} style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}></textarea>
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <label className="info-label">Labels (через запятую)</label>
              <input type="text" className="info-input" value={glLabels.join(',')}
                     onChange={e => setGlLabels(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                     disabled={glSubmitting} />
              <small style={{ color: '#888' }}>стандартные: external, non-confirmed, bug, backend, frontend, payment system</small>
            </div>
            <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div>
                <label className="info-label">Исполнитель</label>
                <select className="info-input" value={glAssigneeId} onChange={e => setGlAssigneeId(e.target.value ? Number(e.target.value) : '')} disabled={glSubmitting}>
                  <option value="">— не назначен —</option>
                  {glMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.name || m.username} {m.username ? `(@${m.username})` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="info-label">Этап (milestone)</label>
                <select className="info-input" value={glMilestoneId} onChange={e => setGlMilestoneId(e.target.value ? Number(e.target.value) : '')} disabled={glSubmitting}>
                  <option value="">— нет —</option>
                  {glMilestones.map(m => (
                    <option key={m.id} value={m.id}>{m.title}{m.due_date ? ` (до ${m.due_date})` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="info-label">Дата (due date)</label>
                <input type="date" className="info-input" value={glDueDate} onChange={e => setGlDueDate(e.target.value)} disabled={glSubmitting} />
              </div>
              {!glEditingId && (
                <div>
                  <label className="info-label">Учёт времени</label>
                  <input type="text" className="info-input" placeholder="3h30m / 2d / 90m" value={glTimeEstimate} onChange={e => setGlTimeEstimate(e.target.value)} disabled={glSubmitting} />
                  <small style={{ color: '#888' }}>формат GitLab: 3h30m, 2d, 90m</small>
                </div>
              )}
              {!glEditingId && (
                <div style={{ gridColumn: '1 / span 2' }}>
                  <label className="info-label">Родитель (iid связанного issue, link «is_blocked_by»)</label>
                  <input type="number" className="info-input" placeholder="например 142" value={glParentIid} onChange={e => setGlParentIid(e.target.value ? Number(e.target.value) : '')} disabled={glSubmitting} />
                </div>
              )}
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', alignItems: 'center' }}>
              {glEditingId && (
                glEditingState === 'opened' ? (
                  <button type="button" className="btn btn-outline-secondary" style={{ marginRight: 'auto' }}
                          onClick={() => changeGitlabIssueState(glEditingId, 'close')} disabled={glSubmitting}>
                    ✔ Закрыть issue
                  </button>
                ) : (
                  <button type="button" className="btn btn-outline-success" style={{ marginRight: 'auto' }}
                          onClick={() => changeGitlabIssueState(glEditingId, 'reopen')} disabled={glSubmitting}>
                    ↻ Переоткрыть
                  </button>
                )
              )}
              <button type="button" className="btn btn-secondary" onClick={() => setShowGitlabModal(false)} disabled={glSubmitting}>Отмена</button>
              <button type="button" className="btn btn-primary" onClick={submitGitlabIssue} disabled={glSubmitting || !glTitle || !glDescription}>
                {glSubmitting ? (glEditingId ? 'Сохраняем…' : 'Создаём…') : (glEditingId ? 'Сохранить' : 'Создать issue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI-разбор — модалка */}
      {showAiModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => !aiLoading && setShowAiModal(false)}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '8px', width: '90%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto' }}
               onClick={(e) => e.stopPropagation()}>
            <h5 style={{ marginTop: 0 }}>
              🤖 Разбор от AI{aiTicketNumber != null ? ` (обращение №${aiTicketNumber})` : ''}
            </h5>
            {aiLoading && (
              <div style={{ color: '#666', padding: '1rem 0' }}>Думаю…</div>
            )}
            {!aiLoading && aiError && (
              <div style={{ background: '#fee', border: '1px solid #fcc', padding: '0.5rem', borderRadius: 4, margin: '0.5rem 0', color: '#900' }}>{aiError}</div>
            )}
            {!aiLoading && !aiError && aiAnalysis != null && (
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.5, color: '#222', marginTop: '0.5rem' }}>
                {aiAnalysis}
              </div>
            )}
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', alignItems: 'center' }}>
              {!aiLoading && (aiError || aiAnalysis != null) && (
                <button type="button" className="btn btn-outline-secondary" style={{ marginRight: 'auto' }}
                        onClick={requestAiAnalysis} disabled={aiLoading}>
                  ↻ Перегенерировать
                </button>
              )}
              {!aiLoading && !aiError && aiAnalysis != null && (
                <button type="button" className="btn btn-outline-primary" onClick={copyAiAnalysis}>
                  {aiCopied ? '✓ Скопировано' : '📋 Скопировать'}
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={() => setShowAiModal(false)} disabled={aiLoading}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* История обращений */}
      {Array.isArray((chat.chat_metadata as any)?.sessions) && ((chat.chat_metadata as any).sessions as any[]).length > 0 && (
        <div className="info-field" style={{ margin: '0 1rem 0.5rem' }}>
          <label className="info-label">История обращений</label>
          <div className="sessions-list">
            {((chat.chat_metadata as any).sessions as any[]).map((s: any) => {
              const openedDate = s.opened_at
                ? new Date(s.opened_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                : '—';
              const isCurrent = !s.closed_at;
              return (
                <div
                  key={s.ticket}
                  className={`session-item ${isCurrent ? 'session-open' : 'session-closed'}`}
                  onClick={() => s.opened_msg_id && onJumpToMessage?.(s.opened_msg_id)}
                  title={s.opened_msg_id ? 'Нажмите, чтобы перейти к обращению' : ''}
                >
                  <span className="session-num">№{s.ticket}</span>
                  <span className="session-date">{openedDate}</span>
                  <span className={`session-status ${isCurrent ? 'open' : 'closed'}`}>
                    <i className={`bi ${isCurrent ? 'bi-circle-fill' : 'bi-check-lg'}`}></i>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Модалки */}
      {showTransferModal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Передать чат</h3>
              <button onClick={() => setShowTransferModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <label>Выберите пользователя:</label>
              <select value={selectedUserId || ''} onChange={(e) => setSelectedUserId(Number(e.target.value))}>
                <option value="">-- Выберите --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.login} ({u.is_admin ? 'Администратор' : 'Оператор'})</option>
                ))}
              </select>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowTransferModal(false)} className="btn-secondary">Отмена</button>
              <button onClick={handleTransferChat} className="btn-primary" disabled={!selectedUserId}>Передать</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Главный компонент Dashboard
// Телефон определяем по тому же порогу, что и CSS (@media max-width: 768px):
// на узком экране третьей колонки нет, панели раскрываются поверх переписки.
const isNarrowScreen = () => typeof window !== 'undefined' && window.innerWidth <= 768;

const DashboardNew: React.FC = () => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [currentGroup, setCurrentGroup] = useState<number | null>(() => getStoredDashboardGroup());
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ active: 0, queue: 0 });
  const [showInfo, setShowInfo] = useState(false);
  const [messagesReloadTrigger, setMessagesReloadTrigger] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [jumpToMsgId, setJumpToMsgId] = useState<number | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const chatsRef = useRef<Chat[]>([]);
  const selectedChatRef = useRef<Chat | null>(null);
  const currentGroupRef = useRef<number | null>(null);
  const messagesReloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced reload messages - предотвращает слишком частые перезагрузки
  const scheduleMessagesReload = () => {
    if (messagesReloadTimeoutRef.current) {
      clearTimeout(messagesReloadTimeoutRef.current);
    }
    messagesReloadTimeoutRef.current = setTimeout(() => {
      setMessagesReloadTrigger(prev => prev + 1);
    }, 300); // Ждем 300ms перед перезагрузкой
  };

  // Immediate reload for edit/delete/pin actions (no debounce)
  const reloadMessagesImmediate = () => {
    if (messagesReloadTimeoutRef.current) {
      clearTimeout(messagesReloadTimeoutRef.current);
    }
    setMessagesReloadTrigger(prev => prev + 1);
  };

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    currentGroupRef.current = currentGroup;
  }, [currentGroup]);

  useEffect(() => {
    if (currentGroup !== null) {
      localStorage.setItem(DASHBOARD_CURRENT_GROUP_KEY, String(currentGroup));
    }
  }, [currentGroup]);

  // Socket.IO connection
  useEffect(() => {
    socketService.connect();

    // Handle new messages
    const handleNewMessage = (data: any) => {
      const chatId = Number(data.chat_id);
      const targetChat = chatsRef.current.find(c => c.id === chatId);
      const eventGroupId = Number(data?.chat_group_id);
      const resolvedGroupId = targetChat?.group_id || (Number.isInteger(eventGroupId) ? eventGroupId : undefined);
      const isIncoming = !data?.message?.local_user_id;
      const isActiveChat = !!(selectedChatRef.current && chatId === selectedChatRef.current.id);

      // Для исходящих сообщений не увеличиваем unread-счётчики
      if (!isIncoming) {
        if (isActiveChat) {
          scheduleMessagesReload();
        }
        return;
      }

      // Если чат уже открыт, считаем сообщение прочитанным сразу
      if (isActiveChat) {
        // Обновляем ленту сообщений
        scheduleMessagesReload();
        // Keep backend unread state in sync for active chat.
        api.post(`/api/chats/${chatId}/read`).catch(() => {});
        return;
      }
      
      // Обновляем счетчики локально сразу, чтобы не ждать повторного API refresh
      setChats(prev => prev.map(chat =>
        chat.id === chatId
          ? { ...chat, unread_count: (chat.unread_count || 0) + 1 }
          : chat
      ));

      setSelectedChat(prev => (
        prev && prev.id === chatId
          ? { ...prev, unread_count: (prev.unread_count || 0) + 1 }
          : prev
      ));

      if (resolvedGroupId) {
        setGroups(prev => prev.map(group =>
          group.id === resolvedGroupId
            ? { ...group, unread_count: (group.unread_count || 0) + 1 }
            : group
        ));
      }

      // If viewing this chat, reload messages area only (with debounce)
      if (selectedChatRef.current && chatId === selectedChatRef.current.id) {
        scheduleMessagesReload();
      }

      // Синхронизируем агрегированную статистику с небольшой задержкой,
      // чтобы не перетереть optimistic unread апдейт гонкой запросов.
      setTimeout(() => {
        loadGroups();
        if (currentGroupRef.current !== null) {
          loadChatsByGroup(currentGroupRef.current, true);
        }
        loadStats();
      }, 500);
    };

    // Handle chat updates
    const handleChatUpdate = (data: any) => {
      if (currentGroupRef.current !== null) {
        loadChatsByGroup(currentGroupRef.current, true); // keepSelection = true
      }
      loadStats();
    };

    const handleMessageEdited = (data: any) => {
      console.log('Message edited:', data);
      // Reload messages immediately if viewing the chat
      if (selectedChatRef.current && data.chat_id === selectedChatRef.current.id) {
        reloadMessagesImmediate();
      }
    };

    const handleMessagePinned = (data: any) => {
      console.log('Message pinned:', data);
      // Reload messages immediately if viewing the chat
      if (selectedChatRef.current && data.chat_id === selectedChatRef.current.id) {
        reloadMessagesImmediate();
      }
    };

    const handleMessageDeleted = (data: any) => {
      console.log('Message deleted:', data);
      // Reload messages immediately if viewing the chat
      if (selectedChatRef.current && data.chat_id === selectedChatRef.current.id) {
        reloadMessagesImmediate();
      }
    };

    // Переподключились после разрыва: события, прилетевшие за это время, socket.io
    // не переигрывает — они потеряны. Без этого лента оставалась замороженной до F5.
    const handleResync = () => {
      reloadMessagesImmediate();
      if (currentGroupRef.current) {
        loadChatsByGroup(currentGroupRef.current, true);
      }
    };

    socketService.onNewMessage(handleNewMessage);
    socketService.onChatUpdate(handleChatUpdate);
    socketService.onMessageEdited(handleMessageEdited);
    socketService.onMessagePinned(handleMessagePinned);
    socketService.onMessageDeleted(handleMessageDeleted);
    socketService.onReconnect(handleResync);

    return () => {
      socketService.off('new_message', handleNewMessage);
      socketService.off('chat_update', handleChatUpdate);
      socketService.off('message_edited', handleMessageEdited);
      socketService.off('message_pinned', handleMessagePinned);
      socketService.off('message_deleted', handleMessageDeleted);
      socketService.offReconnect(handleResync);
      // Очищаем таймер при размонтировании
      if (messagesReloadTimeoutRef.current) {
        clearTimeout(messagesReloadTimeoutRef.current);
      }
    };
  }, []);

  // Страховочный опрос. Сокет умеет умирать МОЛЧА: TCP-соединение висит, socket.io
  // считает себя живым, событий нет и 'connect' больше не сработает — значит и
  // догрузка по переподключению не спасёт. Раз в 25с тихо сверяем ленту открытого
  // чата и список. Стоит копейки, зато лента не замирает никогда.
  useEffect(() => {
    const POLL_MS = 25000;
    const timer = setInterval(() => {
      // Вкладка в фоне — оператор всё равно не смотрит, не тратим запросы.
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      reloadMessagesImmediate();
      if (currentGroupRef.current) {
        loadChatsByGroup(currentGroupRef.current, true);
      }
    }, POLL_MS);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadGroups();
    loadStats();
  }, []);


  useEffect(() => {
    if (currentGroup !== null) {
      loadChatsByGroup(currentGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroup]);

  const loadGroups = async () => {
    try {
      setLoadError(null);
      const response = await api.get('/api/groups');
      const groupsData = response.data || [];
      setGroups(groupsData);

      // Важно: используем functional update, чтобы избежать stale-closure
      // в socket handlers и не переключать текущую группу самопроизвольно.
      setCurrentGroup((prev) => {
        if (prev !== null) return prev;
        if (groupsData.length === 0) return null;
        const savedGroupId = Number(localStorage.getItem(DASHBOARD_CURRENT_GROUP_KEY));
        if (Number.isInteger(savedGroupId) && groupsData.some((g: Group) => g.id === savedGroupId)) {
          return savedGroupId;
        }
        const staffGroup = groupsData.find((g: Group) => g.group_name === 'Персонал');
        return staffGroup ? staffGroup.id : groupsData[0].id;
      });
    } catch (error: any) {
      console.error('Ошибка загрузки групп:', error);
      if (error?.response?.status === 429) {
        setLoadError('Сервер перенагружен. Пожалуйста, подождите и обновите страницу.');
      } else {
        setLoadError('Ошибка загрузки данных.');
      }
    }
  };

  const loadChatsByGroup = async (groupId: number, keepSelection = false) => {
    try {
      setLoadError(null);
      const response = await api.get(`/api/groups/${groupId}/chats`);
      const nextChats = response.data || [];
      setChats(nextChats);
      if (keepSelection) {
        setSelectedChat(prev => {
          if (!prev) return prev;
          const updated = nextChats.find((c: Chat) => c.id === prev.id);
          return updated || prev;
        });
      }
      if (!keepSelection) {
        setSelectedChat(null);
        setShowInfo(false);
      }
      loadStats();
    } catch (error: any) {
      console.error('Ошибка загрузки чатов:', error);
      if (error?.response?.status === 429) {
        setLoadError('Сервер перенагружен. Пожалуйста, подождите и обновите страницу.');
      } else {
        setLoadError('Ошибка загрузки данных.');
      }
    }
  };

  const handleToggleFavorite = async (chat: Chat) => {
    const nextPinnedState = !chat.is_pinned;

    setChats(prev => prev.map(item => (
      item.id === chat.id ? { ...item, is_pinned: nextPinnedState } : item
    )));
    setSelectedChat(prev => (
      prev && prev.id === chat.id ? { ...prev, is_pinned: nextPinnedState } : prev
    ));

    try {
      await api.post(`/api/chats/${chat.id}/${nextPinnedState ? 'pin' : 'unpin'}`);
      if (currentGroup !== null) {
        loadChatsByGroup(currentGroup, true);
      }
      loadGroups();
    } catch (error) {
      console.error('Ошибка изменения избранного:', error);
      setChats(prev => prev.map(item => (
        item.id === chat.id ? { ...item, is_pinned: chat.is_pinned } : item
      )));
      setSelectedChat(prev => (
        prev && prev.id === chat.id ? { ...prev, is_pinned: chat.is_pinned } : prev
      ));
    }
  };

  const loadStats = async () => {
    try {
      setLoadError(null);
      const response = await api.get('/api/chats');
      const chatsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const externalChats = chatsData.filter((c: Chat) => !c.is_staff);
      const active = externalChats.filter((c: Chat) => {
        const status = c.status || (c.is_active === false ? 'closed' : 'open');
        return status === 'open';
      }).length;
      const queue = externalChats.filter((c: Chat) => !c.assigned_to).length;
      setStats({ active, queue });
    } catch (error: any) {
      console.error('Ошибка загрузки статистики:', error);
      if (error?.response?.status === 429) {
        setLoadError('Сервер перенагружен. Пожалуйста, подождите и обновите страницу.');
      } else {
        setLoadError('Ошибка загрузки данных.');
      }
    }
  };

  const handleChatSelect = async (chat: Chat) => {
    // Устанавливаем chat и показываем панель сразу.
    // 🔴 На телефоне карточку НЕ открываем: там она не третья колонка, а панель во весь
    // экран поверх переписки — открыв чат, оператор видел бы карточку вместо сообщений.
    // Открывается кнопкой «Информация» в шапке.
    setSelectedChat(chat);
    setShowInfo(!isNarrowScreen());

    // Mark chat as read
    try {
      const unreadBefore = chat.unread_count || 0;
      await api.post(`/api/chats/${chat.id}/read`);
      // Обновляем счетчики без перезагрузки групп (но НЕ перезаписываем selectedChat, чтобы не скрыть панель)
      setChats(prev => prev.map(c => (c.id === chat.id ? { ...c, unread_count: 0 } : c)));
      setSelectedChat(prev => (prev && prev.id === chat.id ? { ...prev, unread_count: 0 } : prev));

      if (chat.group_id && unreadBefore > 0) {
        setGroups(prev => prev.map(g =>
          g.id === chat.group_id
            ? { ...g, unread_count: Math.max(0, (g.unread_count || 0) - unreadBefore) }
            : g
        ));
      }

      // Принудительная синхронизация с сервером (устраняет рассинхрон без F5)
      loadGroups();
      if (currentGroup !== null) {
        loadChatsByGroup(currentGroup, true);
      }
    } catch (error) {
      console.error('Ошибка отметки чата как прочитанного:', error);
    }
  };

  // Авто-выбор чата при переходе из раздела «Тикеты» (?chat=<id>)
  useEffect(() => {
    const chatIdRaw = new URLSearchParams(window.location.search).get('chat');
    const chatId = chatIdRaw ? Number(chatIdRaw) : 0;
    if (!chatId) return;
    api.get(`/api/chats/${chatId}`).then(res => {
      const chat = (res.data && (res.data.data || res.data)) as Chat;
      if (chat && chat.id) {
        setSelectedChat(chat);
        // Тот же случай, но для перехода по ссылке ?chat=NN из SLA-уведомления —
        // на телефоне сразу показываем переписку, а не карточку поверх неё.
        setShowInfo(!isNarrowScreen());
        window.history.replaceState({}, '', '/dashboard');
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentGroupName = groups.find(g => g.id === currentGroup)?.group_name || 'Чаты';

  return (
    <div className="dashboard">
      {loadError && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#ef4444',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: '0.95rem',
          maxWidth: '90%'
        }}>
          ⚠️ {loadError}
        </div>
      )}
      <Sidebar
        groups={groups}
        currentGroup={currentGroup}
        onGroupSelect={setCurrentGroup}
        stats={stats}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <div className={`chats-container-mobile ${selectedChat ? 'chat-selected' : ''}`}>
        <ChatsList
          chats={chats}
          groups={groups}
          currentGroup={currentGroup}
          onGroupSelect={setCurrentGroup}
          selectedChat={selectedChat}
          onChatSelect={handleChatSelect}
          onToggleFavorite={handleToggleFavorite}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          currentGroupName={currentGroupName}
          onMobileSidebarOpen={() => setMobileSidebarOpen(true)}
        />
      </div>

      <div className={`messages-container-mobile ${selectedChat ? 'chat-selected' : ''}`}>
        <MessagesArea
          chat={selectedChat}
          onMessageSent={() => {
            if (currentGroup !== null) {
              loadChatsByGroup(currentGroup, true); // keepSelection = true
            } else {
              loadStats();
            }
          }}
          showInfo={showInfo}
          onToggleInfo={() => setShowInfo(!showInfo)}
          messagesReloadTrigger={messagesReloadTrigger}
          onBackToList={() => {
            setSelectedChat(null);
            setShowInfo(false);
          }}
          jumpToMessageId={jumpToMsgId}
          onJumpHandled={() => setJumpToMsgId(null)}
        />
      </div>

      {showInfo && (
        <div className="info-panel-container">
          <InfoPanel
            chat={selectedChat}
            onClose={() => setShowInfo(false)}
            onChatUpdate={() => {
              if (currentGroup !== null) {
                loadChatsByGroup(currentGroup, true);
              }
            }}
            currentGroup={currentGroup}
            onSelectChatChanged={(chat) => setSelectedChat(chat)}
            onChatPatched={(updatedChat) => {
              setChats(prev => {
                const belongsToCurrentGroup = currentGroup === null || updatedChat.group_id === currentGroup;
                if (!belongsToCurrentGroup) {
                  return prev.filter(chat => chat.id !== updatedChat.id);
                }

                return prev.map(chat => (
                  chat.id === updatedChat.id ? { ...chat, ...updatedChat } : chat
                ));
              });
            }}
            onShowInfoChanged={(show) => setShowInfo(show)}
            onJumpToMessage={(id) => setJumpToMsgId(id)}
            onInsertText={(t) => composerBridge.insert?.(t)}
          />
        </div>
      )}
    </div>
  );
};

export default DashboardNew;
